/**
 * The **narrator** (`resolution-narration` 1.3) — the one place that turns a resolution's
 * recorded {@link ResolvedStep} list + {@link ResolutionAbout} breadcrumb into the shared
 * **story line + receipt** prose (`projects/resolution-narration/spec.md`). Both the browser
 * card (`ResultCard.tsx`, 1.4) and the CLI (`attackOutcome.ts`, 1.5) render off this — one
 * narrator, one voice, everywhere.
 *
 * **Governing rule, load-bearing: the record is the ONLY input.** No compendium lookup, no live
 * encounter state, no re-rolling, no recomputing a number the record already carries. If a fact
 * the prose wants (a rider's true name, post-apply HP, who granted resistance/advantage) is not on
 * a step or on `about`, that fact is not available in Phase 1 — the answer is to degrade honestly
 * (never `undefined`, never a crash), not to invent or re-derive it. See `steps.ts`'s own doc
 * comments (`DamageStep`, `HpDeltaStep`) for exactly which facts are and are not recorded, and why.
 *
 * **Scope: every resolution the engine commits** (`narration-ledger-retirement` 3.3). A step list
 * with a `to_hit` (or a `miss_chance` standing in for one) narrates as an attack; one with neither
 * narrates as a head-less resolution ({@link buildHeadlessStory}, off the record's `about.action`).
 * {@link narrateResolution} never declines: the ledger it used to fall back to is gone. **Both
 * defenses narrate**: a weapon attack vs Evasion and a TECH attack vs E-Def (2.3), which differ in
 * the story line's verb ({@link attackClause}) and in one rendered word on the TO HIT row, not in
 * shape.
 *
 * **Two shapes, one voice.** ONE `to_hit` is the Phase 1 single-target weapon attack: one story
 * line, one receipt. TWO OR MORE is an AREA fan-out (Phase 2.1) — the story line names the shape
 * word off `about.weapon.area` and every caught target's own outcome, and the receipt repeats the
 * same rows once per target under a `vs <name>` divider. Nothing is shared across targets: each
 * target's DAMAGE/ARMOR/RESULT rows read only its OWN segment's steps, which is what keeps
 * `projects/completed/aoe-attack-headline.md`'s invariant (no cross-target total on a per-target
 * line) true by construction rather than by care.
 *
 * **A BARRAGE is a third framing over those same two shapes, not a third shape** (Phase 2.2). Since
 * `barrage-sequential-resolution` (`decisions/0049`) every Barrage shot is its own
 * independently-committed resolution carrying exactly one `to_hit`, so a Barrage reaches here as an
 * ordinary single-target attack (or, for a barraged area weapon, as an ordinary area fan-out) plus
 * two recorded facts on the breadcrumb — `about.barrage` and `about.noBonus` — which
 * {@link chainRows} turns into the receipt's opening `BARRAGE` / `AUX RIDER` rows. It is
 * recognized from the RECORD, never from a caller passing the right `kind`.
 *
 * **Every op renders, and the compiler holds that** ({@link NARRATED_OPS}). Until 3.3 this module
 * gated on a fail-closed op allowlist, because a narrated card once silently dropped a forced
 * save and the status it landed (`resolution-narration` 1.4/1.5 close-out). `decisions/0177`
 * replaced "decline" with "render it right", and every op now has a renderer; the allowlist became
 * a `satisfies Record<op, true>` table, so a new `ResolvedStep` op fails to compile here until it
 * gets one.
 *
 * **Rendering contract for the receipt (C1, `projects/resolution-narration.md` 1.3):** every
 * {@link ReceiptRow.text} is a PLAIN STRING with its inner column alignment already baked in as
 * literal padding — the whole receipt's column widths are computed once, in a single FINAL pass
 * ({@link padReceipt}, split from content composition for reversibility — see its own doc comment,
 * F, resolution-narration 1.4/1.5 close-out). A renderer must present the receipt as a
 * **monospace, `white-space: pre-wrap` block** (or the CLI's plain terminal output) —
 * reformatting/wrapping the text with a proportional font or its own layout will misalign the
 * padding this module computed. That is 1.4/1.5's job to honor, not this module's to enforce; if
 * that contract is ever relaxed, `padReceipt` is the one thing to remove, not something to fight.
 */
import type {
  AffordanceUseSetStep,
  CostWaivedStep,
  AiCascadeCheckStep,
  BurnSetStep,
  Cell,
  CombatantId,
  ContestStep,
  DamageStep as DomainDamageStep,
  DestroyItemStep,
  DestroyStep,
  ForcedMoveStep,
  HeatDeltaStep,
  HpDeltaStep,
  InterceptStep,
  LinkStep,
  MissChanceStep,
  MoveStep,
  ResolutionAbout,
  ResolvedStep,
  ResourceSetStep,
  SaveStep,
  StatusAddStep,
  StatusExpiry,
  StatusRemoveStep,
  StressDeltaStep,
  StructureDeltaStep,
  TableCheckStep,
  ToHitStep,
} from "@lancer-vtt/domain";
import { GM_ACTOR, loadedResourceKey, UNDISCLOSED_FEATURE_NAME, UNDISCLOSED_WEAPON_NAME } from "@lancer-vtt/domain";
import { rollVerdict } from "./verdict";

/** Resolve a combatant id to a display name; defaults to the raw id when absent. */
export type NameOf = (id: CombatantId) => string;

/** One row of the receipt — a label in words, its text (a plain string, never markup). */
export type ReceiptRow = {
  /** Short label in words: "TO HIT" | "DAMAGE" | "ARMOR" | "RESIST" | "IMMUNE" | "HEAT" | "RESULT" | … */
  label: string;
  /** The row's content. A plain string — never markup. Column alignment (C1) is already baked in. */
  text: string;
  /** A provenance/continuation of the row above: unlabelled and indented when rendered. */
  sub?: boolean;
  /**
   * A **per-target divider** (`vs <name>`), not a row of content — emitted only by a multi-target
   * area attack (`resolution-narration` 2.1) to open each caught target's own block of rows. A
   * renderer prints it flush at the LABEL column with no label of its own (the CLI's
   * {@link import("../../../../../cli/src/attackOutcome").receiptLine}) and may style it as a
   * heading; it carries no numbers, so nothing about it participates in column alignment.
   */
  divider?: boolean;
  /**
   * Optional accent for a renderer to colour (the to-hit outcome, a crit, a save's verdict).
   *
   * **`pass`/`fail` are the SAVE row's two values** (`resolution-narration` 3.1) and, since 3.3b,
   * the `PAINT JOB` row's (a fired interception that ignored the hit is `pass`, one that came up
   * short is `fail` — the same "good for the defender" axis a save's verdict sits on). They are
   * deliberately NOT folded onto `hit`/`miss`: a save's verdict is good or bad for the OPPOSITE
   * combatant from the attack's, so painting a passed save with the attacker's hit colour would
   * invert the one thing the accent is for. They are the same two words `ResultCard.tsx`'s ledger
   * `ResultLine.outcome` has always carried for its own save line, so the browser's `outcomeClass`
   * map and its `.rc-pass`/`.rc-fail` CSS already existed — this widening lights them up rather
   * than adding a surface. The CLI reads no accent at all; the verdict is in the row's text.
   */
  emphasis?: "miss" | "hit" | "crit" | "pass" | "fail";
  /**
   * **The row's right-hand running number**, as a plain string (`"13"` for a TO HIT total, `"5"`
   * for a DAMAGE row's roll, `"3"` for an ARMOR row's post-armor figure) — `comms-attack-reaction-
   * cards` 2.2. Present only on rows that END on such a number (TO HIT, DAMAGE/HEAT, a
   * reducing ARMOR, RESIST, a rolled SAVE), and only when the caller asked for
   * `narrateResolution({ structured: true })`.
   *
   * **Why a separate field and not a parse of {@link text}.** `text` has the C1 column padding
   * baked in (literal space runs sized for a monospace block), so a card that lays the receipt out
   * as a label / explanation / number grid would otherwise have to split on those runs — which is
   * exactly the coupling `padReceipt`'s doc comment warns against. `text` is unchanged either way:
   * the CLI keeps printing it and the golden text does not move.
   */
  value?: string;
  /**
   * The row's explanation WITHOUT {@link value} and without column padding — the middle cell of
   * the same grid. Present exactly when `value` is.
   */
  detail?: string;
  /**
   * An accuracy/difficulty provenance sub-row's parts (`comms-attack-reaction-cards` 3.2): the
   * signed net count (`+1` Accuracy, `-2` Difficulty) and each named source as the row's
   * {@link text} spells it (`Accurate +1`, `soft cover −1`, `Bondmate +1 via Vex`), so a card can
   * print the net as the row's label and each source as an unbreakable chip. Structured-only, like
   * {@link value}.
   */
  accuracy?: { net: number; sources: string[] };
  /**
   * A RESIST row whose halving changed nothing (`1 → 1`: `Math.ceil` of a halved 1 is 1, settled
   * item 9). The row still renders (`decisions/0177`: every op renders); a card mutes it.
   * Structured-only, like {@link value}.
   */
  noChange?: true;
};

/** The narrator's output: one story sentence, its outcome accent, and the itemized receipt. */
export type NarratedResolution = {
  /** One sentence, outcome first. Plain string, never markup. */
  story: string;
  /**
   * The story's outcome accent, when it has one. **Deliberately absent for a multi-target area
   * attack** (`resolution-narration` 2.1): its story line carries one outcome PER caught target
   * (`Goblin-2 HIT 7, Goblin-3 MISS`), so there is no single accent that describes the sentence —
   * colouring the whole line by, say, the first target's result would be the same class of lie
   * `projects/completed/aoe-attack-headline.md` fixed. Each target's own `TO HIT` receipt row
   * still carries its `emphasis`, which is where the per-target accent belongs.
   */
  outcome?: "miss" | "hit" | "crit";
  receipt: ReceiptRow[];
};

type DamageStep = Extract<ResolvedStep, { op: "damage" }>;

/**
 * **Every {@link ResolvedStep.op}, each with the renderer that reads it** (`decisions/0177`). The
 * `satisfies Record<op, true>` is the point: a new op in the `ResolvedStep` union fails to compile
 * here until someone adds it, and adding it is the moment to write its row. Until
 * `narration-ledger-retirement` 3.3 this was a runtime allowlist a resolution had to pass to narrate;
 * every op had earned a renderer by then, so the gate went and the exhaustiveness check stayed.
 *
 * **`resource_delta` is listed but deliberately UNRENDERED.** Every real attack carries one
 * (`attack_ordinal_this_turn`, folded on by `refereeIntent.ts`), and capped modifiers add more;
 * both are engine bookkeeping (`resolve/types.ts`'s `Resolved.ordinalIncrement`: "inert"). The old
 * ledger hid them behind a collapsed COMBAT AUDIT fold; the narrator leaves them out. If a
 * `resource_delta` ever carries something a player should see, that is a new row, not a reason to
 * drop the op.
 */
export const NARRATED_OPS = {
  to_hit: true,
  damage: true,
  hp_delta: true,
  resource_delta: true,
  // resolution-narration 2.5 (`decisions/0177`) — the consequence ops, each with a real
  // renderer below: `consequenceRow` for the first four, `missChanceRow` for the last.
  status_add: true,
  status_remove: true,
  heat_delta: true,
  resource_set: true,
  miss_chance: true,
  // resolution-narration 3.1 (`decisions/0177`) — the forced save, with the per-source
  // itemization the to-hit arm got: `saveRow` + `saveProvenanceRow` below.
  save: true,
  // resolution-narration 3.2 + 3.3b (`decisions/0177`) — the structure/stress CASCADE, admitted
  // as ONE narrative unit because "Goblin-3 loses a structure; on the structure table it rolls…"
  // and the `PAINT JOB` row are the same paragraph. Renderers: `trackDeltaRow`, `tableCheckRow`,
  // `cascadeCheckRow`, `destroyRow`, `destroyItemRow`, `interceptRow`, `chargeRow` — plus
  // `recoverRow` for the POSITIVE `hp_delta` the cascade's "your HP resets to full" arm and a
  // successful interception both emit (`hp_delta` was already admitted; only the negative half
  // had a row before 3.2).
  structure_delta: true,
  stress_delta: true,
  table_check: true,
  ai_cascade_check: true,
  destroy: true,
  destroy_item: true,
  affordance_use_set: true,
  // resolution-events 2.6 (`decisions/0211`) — a charge cost a replacement waived: `waivedRow`.
  cost_waived: true,
  intercept: true,
  // resolution-narration 3.4 (`decisions/0177`) — the DISPLACEMENT/BURN group, which every
  // earlier roadmap line left unscoped and which held out every knockback weapon. Renderers:
  // `moveRow`, `forcedMoveRow`, `burnRow`.
  move: true,
  move_budget_delta: true,
  forced_move: true,
  burn_set: true,
  // resolution-narration 3.4 — the remaining eleven, each with a row in `residueRow`
  // (`contest` in `contestRows`, which emits a verdict row plus one sub-row per side).
  contest: true,
  discharge_set: true,
  status_suspend_set: true,
  resource_clear: true,
  mine_armed_set: true,
  concealed_set: true,
  disclosure_set: true,
  features_revealed: true,
  spawn_object: true,
  despawn_object: true,
  area_add: true,
  area_remove: true,
  // chained-resolution-sequence 1.2 (`decisions/0210`) — a `link` renders no row of its own:
  // `narrateResolution` splits the list at it and opens each link's block with a divider.
  link: true,
} as const satisfies Record<ResolvedStep["op"], true>;

/**
 * The ops {@link consequenceRow} renders as the receipt's trailing **CONSEQUENCES** block —
 * what the shot spent, moved, or landed, as opposed to how it rolled and what it dealt.
 *
 * **They are collected from the WHOLE step list and emitted ONCE at the end, never per
 * `vs <target>` block.** Two of them are recorded once per FIRE rather than once per caught
 * target (a Loading weapon's `resource_set` and a `tg_heat_self`/Overkill `heat_delta` are both
 * folded on after an area fan-out completes — `resolve/weaponConsume.ts`, measured off a real
 * capture), so attributing them to whichever segment they happened to land in would put "Bret
 * heat +1" under a `vs Goblin-5` heading it has nothing to do with. Every consequence row names
 * its own subject, so a trailing block loses nothing.
 */
const CONSEQUENCE_OPS: ReadonlySet<ResolvedStep["op"]> = new Set(["status_add", "status_remove", "heat_delta", "resource_set"]);

/** The `current.resources` key prefix a weapon's Loading state rides — derived from the one shared key builder so it cannot drift from it. */
const LOADED_KEY_PREFIX = loadedResourceKey("");

/**
 * Segment a resolution's concatenated {@link ResolvedStep `ResolvedStep[]`} at each `to_hit`
 * boundary — the per-target seam a multi-target AREA attack's fan-out produces
 * (`resolveAreaAttack`, `packages/rules/src/encounter/resolve/attack.ts`, concatenates each caught
 * target's own `resolveSingleTarget` steps in exactly this order). Steps before the first `to_hit`
 * fold into `leading` so nothing is dropped.
 *
 * **It is NOT a Barrage's per-shot seam, whatever the older copies of this comment say.** Measured
 * off a real Barrage's `feed.jsonl` (2026-09-06): each Barrage shot is its own resolution with its
 * own single `to_hit` (`barrage-sequential-resolution`, `decisions/0049`), so there is nothing
 * multi-shot here to split; COMMS groups a Barrage's shots over `about.shot` instead
 * (`comms-attack-reaction-cards` 3.3).
 *
 * **This is the canonical copy** (`resolution-narration` 2.1) — callers import it from here
 * rather than keeping their own, because this project's whole premise is that
 * two independently-written summarizers drift (`projects/completed/aoe-attack-headline.md` records
 * the CLI's and the browser's having already diverged once). `cli/src/attackOutcome.ts`'s
 * `attackTargets` reads it for the `--json` `targets[]` data and drops `leading`.
 *
 * **Known edge, ACCEPTED in THIS function — do not "fix" it here.** The Invisible `miss_chance`
 * coin step (`resolve/attackPreRoll.ts`) is recorded only on the MISS face, where that target
 * gets no `to_hit` at all — so a `to_hit`-boundary segmenter attributes the coin to the PREVIOUS
 * target's segment. Pre-existing in every copy of this seam, and this function keeps it: its one
 * outside caller (`cli/src/attackOutcome.ts`'s `attackTargets`) reads no outcome from a coin step,
 * and a target whose only step is the coin simply has no `targets[]` entry.
 *
 * **The narrator itself no longer uses this function to segment** (`resolution-narration` 2.5):
 * it uses the module-private {@link segmentAttack}, which opens a new segment at a `miss_chance`
 * too, because since `decisions/0177` the narrator DOES render the coin and needs it attributed
 * to its own target. `narrateResolution` calls that one; this export stays exactly as
 * it was for its outside callers.
 */
export function segmentAtToHit(steps: readonly ResolvedStep[]): { leading: ResolvedStep[]; segments: ResolvedStep[][] } {
  const segments: ResolvedStep[][] = [];
  const leading: ResolvedStep[] = [];
  let cur: ResolvedStep[] | null = null;
  for (const step of steps) {
    if (step.op === "to_hit") {
      cur = [step];
      segments.push(cur);
    } else if (cur) {
      cur.push(step);
    } else {
      leading.push(step);
    }
  }
  return { leading, segments };
}

/** A segment's head step — the per-target seam the narrator segments on. */
type SegmentHead = ToHitStep | MissChanceStep;

function isSegmentHead(step: ResolvedStep): step is SegmentHead {
  return step.op === "to_hit" || step.op === "miss_chance";
}

/**
 * The narrator's OWN per-target seam (`resolution-narration` 2.5) — {@link segmentAtToHit} plus
 * `miss_chance` as a second boundary.
 *
 * **Why a `miss_chance` is always a segment of its own, not a step inside someone else's.**
 * `rollPreRollMissChance` (`resolve/attackPreRoll.ts`) records the coin ONLY on the losing face,
 * and on that face `resolveSingleTarget` early-outs before the d20 — so a recorded `miss_chance`
 * step is that target's ENTIRE resolution, and it is never followed by a `to_hit` of its own.
 * Under the plain `to_hit`-boundary seam it therefore lands in the PREVIOUS target's segment,
 * which is exactly the mis-attribution {@link segmentAtToHit}'s "known edge" describes. That was
 * harmless while `miss_chance` was outside {@link NARRATED_OPS} (the whole resolution declined);
 * now that the narrator renders the coin, it would print one target's coin under another
 * target's heading, so the narrator splits on it. Measured off a real capture: a Mortar Blast
 * catching two targets, one of them Invisible, records `[to_hit(t1, miss), miss_chance(t2),
 * resource_delta]` — two targets, one `to_hit`.
 *
 * The shared export is deliberately NOT changed to match; see its doc comment for why. Exported
 * module-local (not from the package barrel) for `glance.ts`, which must split targets exactly
 * the way the receipt does.
 */
export function segmentAttack(steps: readonly ResolvedStep[]): { leading: ResolvedStep[]; segments: ResolvedStep[][] } {
  const segments: ResolvedStep[][] = [];
  const leading: ResolvedStep[] = [];
  let cur: ResolvedStep[] | null = null;
  for (const step of steps) {
    if (isSegmentHead(step)) {
      cur = [step];
      segments.push(cur);
    } else if (cur) {
      cur.push(step);
    } else {
      leading.push(step);
    }
  }
  return { leading, segments };
}

// ── id → display name (moved from `ResultCard.tsx`, resolution-narration 1.3) ────────────────

/** Known short compendium/tag/status-family id prefixes stripped before titling (`displaySourceName`). */
const ID_PREFIX = /^(tg|npcf|cb|ms|pg|mw|wm|mf|sys|t)_/;
/** Rank digit → roman numeral, matching the talent-badge convention (`unitBuildView.ts`'s `ROMAN`). */
const RANK_ROMAN = ["", "I", "II", "III", "IV", "V", "VI"];
/**
 * The raw-id grammar: lowercase alphanumerics and underscores, optionally a `:<rank>` suffix.
 * Anything that fails it is a display name, not an id, and {@link displaySourceName} returns it
 * verbatim. Evidence for the grammar (`npc-feature-id-titling` 1.2): every compendium/tag/talent
 * id in `packs/core-rulebook/sidecar/overlay/` matches it, and the only non-matching overlay keys
 * are status-keyed slugs (`sniper-s-mark`, `stay-of-execution`) that never reach a contribution —
 * a status modifier carries the status's DISPLAY name, not its overlay key (`statusOverlayEntry`,
 * `effects/loader.ts`). Note what the grammar admits on purpose: a bare lowercase word such as
 * `weapon` IS a legal id and still titles to "Weapon", which is why a sentinel written into a
 * recorded `name` field has to be more than one word to survive (`accuracy-contribution-weapon-
 * redaction`'s `undisclosed weapon` / `undisclosed feature`).
 */
const RAW_ID = /^[a-z0-9_]+(:\d+)?$/;
/** NPC feature ids alone carry a trailing origin segment — see {@link displaySourceName}'s (b). */
const NPC_FEATURE_PREFIX = /^npcf_/;

/**
 * Human-readable display name for a raw compendium/tag/rider id (`"tg_accurate"`,
 * `"t_crack_shot:2"`, `"ms_hunter_lock"`). Two guards shape it, both added by
 * `npc-feature-id-titling`; the unit table that specifies the whole transform is
 * `narrate.displaySourceName.test.ts`.
 *
 * **(a) A true display name passes through verbatim** — an input that fails the {@link RAW_ID}
 * grammar is returned unchanged, so `"Impaired"`, `"Lock On"`, `"GM Ruling: Called Shot"` and
 * `"Anti-Materiel Rifle"` survive intact. Before this guard the function split on `[:_-]` and
 * re-joined with spaces, which ate a GM ruling's colon (`"GM Ruling  Called Shot"`, double space)
 * and corrupted a hyphen (`"Anti Materiel Rifle"`). Callers should still not RELY on the
 * pass-through: the record's `name` fields are raw ids by contract (`AccuracyContribution.name`,
 * `SaveContribution.name`, rider `DamageStep.source.name` — "deliberately the raw id, not a
 * display name", `steps.ts`), so a display name arriving here is an exception, not the interface.
 *
 * **(b) An `npcf_` id drops its trailing origin segment.** Every NPC feature id in the Massif data
 * ends in `_<owning class or template id>`, so `npcf_bombard_cannon_bombard` read "Bombard Cannon
 * Bombard" at the table. Measured 2026-09-16 over the v3 LCP: 399 of 401 features follow the
 * convention and no class or template id contains an underscore, which is what makes "drop the
 * last segment" and "drop the origin id" the same rule; that corpus claim is a test, not a
 * belief — `packages/compendium/src/npcFeatureOriginSuffix.pin.test.ts`. If stripping would leave
 * nothing (a one-segment id such as `npcf_solo`; none exist in the corpus) the id titles
 * unstripped instead. No other prefix is touched. **Accepted limitation**: a homebrew `.lcp` whose
 * feature ids do not follow Massif's convention loses its last segment wrongly. The base packs are
 * Core + Long Rim and the fielded roster is all Massif, so the residue is cosmetic.
 *
 * Two fielded ids stay wrong after (b) and no id transform can reach them, because the
 * punctuation is not in the id: `npcf_point_defense_shield_cataphract` → "Point Defense Shield"
 * (no hyphen) and `npcf_snipers_mark_sniper` → "Snipers Mark" (no apostrophe). Fixing those needs
 * a frozen display name at spawn, which is its own project.
 *
 * Moved verbatim from `ResultCard.tsx` (resolution-narration 1.3) so both the to-hit/save
 * provenance lines there and this narrator's rider titling share one transform.
 */
export function displaySourceName(id: string): string {
  if (!id) return "an effect";
  if (!RAW_ID.test(id)) return id;
  const segments = id.replace(ID_PREFIX, "").split(/[:_-]/).filter(Boolean);
  const words = NPC_FEATURE_PREFIX.test(id) && segments.length > 1 ? segments.slice(0, -1) : segments;
  if (words.length === 0) return id;
  return words
    .map((w) => {
      const rank = Number(w);
      if (Number.isInteger(rank) && rank > 0 && rank < RANK_ROMAN.length) return RANK_ROMAN[rank];
      return w[0]!.toUpperCase() + w.slice(1);
    })
    .join(" ");
}

// ── number formatting — two distinct signed styles, per the spec's own literal spacing ───────

/** Tight signed form for the accuracy-provenance sub-row: `+2` / `−1` (unicode minus, no space). */
function signedTight(n: number): string {
  return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
}

/** Spaced signed form for the TO HIT row's clauses: `+ 2` / `− 3` (unicode minus, space before the digit). */
function signedSpaced(n: number): string {
  return n >= 0 ? `+ ${n}` : `− ${Math.abs(n)}`;
}

function padEnd(s: string, w: number): string {
  return s.length >= w ? s : s + " ".repeat(w - s.length);
}

// ── TO HIT ─────────────────────────────────────────────────────────────────────────────────

/**
 * The TO HIT row's main line: `d20 N  + B attack bonus  ± A Accuracy|Difficulty die   = total
 * beats|meets|short of Evasion|E-Def N` (the verb from {@link rollVerdict}). Every clause is
 * omitted when its value is `0` — a no-modifier attack reads as just `d20 14   = 14   beats
 * Evasion 10`. Stunned's Evasion cap ({@link ToHitStep.evasionCap}), when present, is named on the
 * defense clause — the record carries the cap value but not which
 * modifier applied it, so the parenthetical names the RAW rule (Stunned), not a source.
 */
function toHitRow(step: ToHitStep): ReceiptRow {
  if (step.forcedMiss !== undefined) {
    const text = "automatic miss — a reaction stopped the attack before it was rolled";
    return { label: "TO HIT", text, emphasis: "miss", value: "miss", detail: text };
  }
  if (step.autoHit === true) {
    const text = "automatic hit — no attack roll";
    return { label: "TO HIT", text, emphasis: "hit", value: "hit", detail: text };
  }
  const d20Part = step.advantageDrop !== undefined ? `d20 ${step.d20} (rolled twice, dropped ${step.advantageDrop})` : `d20 ${step.d20}`;
  const parts = [d20Part];
  if (step.bonus !== 0) parts.push(`${signedSpaced(step.bonus)} attack bonus`);
  if (step.accuracy !== 0) parts.push(`${signedSpaced(step.accuracy)} ${step.accuracy > 0 ? "Accuracy" : "Difficulty"} die`);
  const defenseLabel = step.vs === "edef" ? "E-Def" : "Evasion";
  let vsClause = `${rollVerdict(step.total, step.defense, step.result)} ${defenseLabel} ${step.defense}`;
  if (step.evasionCap !== undefined) vsClause += ` (capped at ${step.evasionCap} — Stunned)`;
  const text = `${parts.join("  ")}   = ${step.total}   ${vsClause}`;
  return { label: "TO HIT", text, emphasis: step.result, value: `${step.total}`, detail: `${parts.join(" ")} ${vsClause}` };
}

/**
 * The accuracy/difficulty provenance sub-row: `N Accuracy|Difficulty net:  source ±v · source ±v
 * · …`. Built from {@link ToHitStep.statusAccuracy} (the net count) and
 * {@link ToHitStep.accuracyContributions} (the per-source breakdown) — omitted entirely when
 * `statusAccuracy` is absent (a no-modifier attack never gets this row).
 *
 * **Every MODIFIER bit comes from `accuracyContributions`** — `statusAccuracy`'s `attacker`/
 * `target`/`weapon` buckets supply the NET only, never an itemized bit (see the `sa.weapon`
 * comment in the body for the double-count that rule fixes). The two exceptions are the two
 * counts that have no `BoundModifier` behind them and therefore never reach that list at all:
 * **cover** (count on `statusAccuracy.cover`, name only on `step.cover`) and **`intent`** (the
 * declared `--accuracy`, 2.3 — see its comment in the body).
 *
 * **Order, a deliberate call, documented because the spec's own two worked examples disagree with
 * each other** (the crit example lists the weapon's innate accuracy first then a target
 * contribution then an attacker contribution then cover; the miss example lists cover first then a
 * target contribution) — this renders a fixed, deterministic order instead: the declared `intent`
 * first, then every accuracy contribution in its recorded array order, then cover last. Report
 * this as a known deviation rather than chase an inconsistent spec.
 *
 * A contribution whose `carrier` is neither the attacker nor the target was **conferred** by an
 * ally (t_spotter:1's Particularize) and is credited `via <ally>` rather than silently attributed
 * to whoever it landed on — mirrors `ResultCard.tsx`'s `contributionParenthetical`.
 */
function accuracyProvenanceRow(step: ToHitStep, names: NameOf): ReceiptRow | undefined {
  const sa = step.statusAccuracy;
  if (!sa) return undefined;
  const net = sa.intent + sa.attacker + sa.target + (sa.cover ?? 0) + (sa.weapon ?? 0);
  const netLabel = net >= 0 ? "Accuracy" : "Difficulty";
  const bits: string[] = [];
  // **`sa.weapon` is NOT itemized here — it is already in `accuracyContributions`.** The firing
  // weapon's own innate Accuracy/Difficulty (`WeaponProfile.accuracy`, NPC weapons only — see its
  // doc comment in `weapons.ts`) is written by `toHit.ts` into BOTH `statusAccuracy.weapon` AND a
  // dedicated `accuracyContributions` entry (`{ name: weapon.id, carrier: attacker.id, side:
  // "attacker" }`), under the SAME `weaponAccuracy !== 0` guard and with the same value — that
  // dual write is the engine's stated contract ("a consumer walking `accuracyContributions` sees
  // it without a third channel") and is pinned against a real spawn by
  // `npcInnateAccuracyRealSpawn.test.ts`. Printing a `sa.weapon` bit as well double-counted it:
  // a real `npcc_bombard` firing its Bombard Cannon printed `1 Difficulty net:  weapon −1 ·
  // Bombard Cannon Bombard −1`, a net that contradicted its own itemization. `net` below still
  // reads `sa.weapon` — the AGGREGATE genuinely lives there and must keep being summed; only the
  // ITEMIZATION moved to the per-source list, which names the weapon better anyway.
  //
  // A mech weapon reaches this row by a different, non-overlapping shape: its Accurate tag folds
  // through the `tg_accurate` overlay modifier into `statusAccuracy.attacker` and appears as a
  // contribution named `"tg_accurate"` ("Accurate +1"), with `statusAccuracy.weapon` absent
  // entirely. Both shapes are pinned in `narrate.golden.test.ts`.
  // **`sa.intent` — the accuracy the attack DECLARED, itemized as `declared ±N`**
  // (`resolution-narration` 2.3, the same defect family as 1.8's double-count and the fourth
  // instance of it). It is the GM's / attacking player's own `--accuracy` flag on the CLI and the
  // "acc/diff" field on COMBAT.app's armed-action panel (`ArmedActionPanel.tsx`) — a situational
  // bonus no rule models, so no `BoundModifier` folds and nothing ever lands in
  // `accuracyContributions` for it. It was already summed into `net` below and named nowhere, so a
  // GM-granted +1 printed a bare `1 Accuracy net` row whose number had no source — exactly the
  // "every number has a name" rule this project exists to enforce. Measured off a real
  // `attack --tech --accuracy 1` against a real backend: `statusAccuracy {intent: 1, attacker: 0,
  // target: 0}` with no `accuracyContributions` key at all.
  //
  // It leads the itemization because it is the declaration the shot was fired under, before any
  // modifier folded. The word is "declared" rather than "GM ruling": a real GM RULING
  // (`consumeOnAttack`) is a different, already-itemized thing that arrives as a named
  // `accuracyContributions` entry ("GM Ruling: Called Shot +1"), and reusing that phrase here
  // would make two distinct sources read as one.
  if (sa.intent !== 0) bits.push(`declared ${signedTight(sa.intent)}`);
  const ownIds: CombatantId[] = [step.attacker, step.target];
  for (const c of step.accuracyContributions ?? []) {
    const conferred = !ownIds.includes(c.carrier);
    bits.push(`${displaySourceName(c.name)} ${signedTight(c.value)}${conferred ? ` via ${names(c.carrier)}` : ""}`);
  }
  if (step.cover !== undefined) {
    bits.push(`${step.cover === "hard" ? "hard cover" : "soft cover"} ${signedTight(sa.cover ?? (step.cover === "hard" ? -2 : -1))}`);
  }
  // A genuinely unmodified roll (net 0, nothing to name) omits this row entirely rather than
  // print a bare "0 Accuracy net" — the real resolver ALWAYS populates `statusAccuracy` (even
  // all-zero; see `toHit.ts`), so without this guard EVERY plain, no-modifier attack would carry
  // this noise (found via `cli/proofs/attack-narration.test.ts`, resolution-narration 1.4/1.5
  // close-out). A net of 0 with something to name (e.g. a +1 and a -1 that cancel) still prints —
  // only the truly empty case is suppressed.
  if (net === 0 && bits.length === 0) return undefined;
  const text = bits.length > 0 ? `${Math.abs(net)} ${netLabel} net:  ${bits.join(" · ")}` : `${Math.abs(net)} ${netLabel} net`;
  return { label: "", text, sub: true, accuracy: { net, sources: bits } };
}

// ── DAMAGE / HEAT ──────────────────────────────────────────────────────────────────────────

/** A damage step whose `source` names a true display name (weapon/effect) — gets its own ARMOR/RESIST/IMMUNE treatment. */
function isNamed(step: DamageStep): step is DamageStep & { source: NonNullable<DomainDamageStep["source"]> } {
  return step.source !== undefined && step.source.kind !== "rider";
}

/** `DAMAGE` for any typed damage; `HEAT` when the type IS heat — heat never renders as damage (the tracker's own rule). */
function labelOf(step: DamageStep): "DAMAGE" | "HEAT" {
  return step.dtype === "heat" ? "HEAT" : "DAMAGE";
}

/**
 * One damage step reduced to the three cells {@link composeDamageBlock} aligns: a name, a
 * descriptor (the dice/bonus/dtype notation, the flat `<N> <dtype>` form when the step rolled no
 * dice, or `"bonus damage"`), and the `= preMitigation`
 * total. **`preMitigation`, never `final`** — the DAMAGE/HEAT row records the roll itself; ARMOR/
 * RESIST/IMMUNE rows (below) narrate what happened to it afterward. For a rider (`source.kind ===
 * "rider"`) `preMitigation === final` always (riders bypass mitigation entirely — see
 * `interpretExtraDamage` in `effects/interpret.ts`, which hardcodes `armor: 0`), so either field
 * would read the same there.
 *
 * **Degradation (the tracker's own rule): a `DamageStep` with no `source` at all degrades to
 * anonymous `"bonus damage"` — never a guess at whether it was really the weapon's own hit.** A
 * `source.kind === "rider"` step titles its carrying-entity id via {@link displaySourceName}
 * (`"ms_hunter_lock"` → `"Hunter Lock"`); a `"weapon"`/`"effect"` step uses `source.name` VERBATIM
 * (a true display name — titling it would corrupt a hyphenated weapon name, C2).
 */
/** The unpadded content of one DAMAGE/HEAT row — the content layer {@link padReceipt} (F) later
 * bakes column alignment onto. Exported (module-local, not from the package barrel) so it stays
 * directly testable independent of presentation, per the reversibility requirement below. */
export type DamagePartsContent = { name: string; descriptor: string; total: string };

export function damageParts(step: DamageStep, opts: { rolled?: boolean } = {}): DamagePartsContent {
  const total = `= ${step.preMitigation}`;
  const riderName = step.source?.kind === "rider" ? displaySourceName(step.source.name) : "";
  // `rolled` (narration-ledger-retirement 3.1): outside an attack there is no weapon hit for a
  // rider or sourceless step to be a BONUS on — a thrown grenade's own damage, a mine's burst — so
  // it prints what it rolled, like a named hit, under the rider's name or none.
  if (!isNamed(step) && opts.rolled !== true) return { name: riderName, descriptor: "bonus damage", total };
  const name = isNamed(step) ? step.source.name : riderName;
  const crit = step.crit ? ", crit" : "";
  // **A DIE-LESS DAMAGE STEP PRINTS NO DICE NOTATION AT ALL** (`resolution-narration` 3.4
  // follow-up, 2026-09-17). `dice: []` is the shape every NPC flat-damage weapon records
  // (`rollInstanceDice` is handed `count: 0`, so it rolls nothing and `damageApply.ts` leaves
  // `sides` unset by construction), and it is also what a Reliable miss floor and an
  // interception's protector share record. Building the header as `` `${dice.length}d${sides ??
  // 6}` `` printed `Napalm Bomb   0d6+4 burn  = 4` for all of them — a d6 the record does not
  // carry and no die was ever rolled against. `decisions/0177`: a wrong row is strictly worse
  // than the honest fallback it replaced, because a fallback reads as "the narrator declined"
  // and a wrong row does not. So the descriptor names only what the step recorded — the flat
  // amount and the damage type (`4 burn`), or the type alone when the step carries no bonus of
  // its own (`bonus: 0` with a nonzero `preMitigation` is the protector share of an intercepted
  // hit: derived math, not a roll, so there is no per-source number to name).
  if (step.dice.length === 0) {
    const flat = step.bonus !== 0 ? `${step.bonus} ` : "";
    return { name, descriptor: `${flat}${step.dtype}${crit}`, total };
  }
  const header = `${step.dice.length}d${step.sides ?? 6}${step.bonus !== 0 ? signedTight(step.bonus) : ""} ${step.dtype}${crit}`;
  const faces = step.dice.map((d) => `[${d}]`).join(" ");
  const bonusPart = step.bonus !== 0 ? ` + ${step.bonus}` : "";
  // A hit that rolled under the weapon's Reliable X deals X: name the floor, or `[1]  = 2` reads as a sum error.
  const floorPart = step.reliable !== undefined ? ` → Reliable ${step.reliable}` : "";
  return { name, descriptor: `${header}   ${faces}${bonusPart}${floorPart}`, total };
}

/**
 * C1's column-alignment: compute the name/descriptor column widths ONCE over a contiguous block
 * of DAMAGE (or HEAT) rows — a weapon's own hit plus any rider bonus damage that rode the same
 * attack — and bake the padding into each row's text. A single-row "block" pads to its own width
 * (a no-op). Gaps (3 spaces name→descriptor, 2 spaces descriptor→total) are taken verbatim from
 * the spec's own worked examples, which this function reproduces exactly for the Anti-Materiel
 * Rifle crit fixture (weapon + Hunter Lock rider) — see the golden test.
 *
 * **Content vs. presentation, deliberately split (F, resolution-narration 1.4/1.5 close-out).**
 * This function is the ONLY place column widths are computed and baked in — {@link damageParts}
 * (its caller's caller) returns plain unpadded content, and {@link padReceipt} is the single final
 * pass that calls this. That split exists for reversibility: the developer picked "story line +
 * receipt" from five RENDERED options and reserved the right to ask for a looser/proportional
 * layout after reading a real station — undoing the baked padding then means deleting the
 * `padReceipt` call and re-locking the goldens, not re-deriving every row's content from scratch.
 */
function composeDamageBlock(items: readonly DamagePartsContent[]): string[] {
  // A genuinely nameless row (a sourceless `DamageStep` — the old-entry degradation case) omits
  // the name column and its gap entirely rather than padding an empty string into leading spaces.
  const named = items.filter((i) => i.name !== "");
  const nameW = named.length > 0 ? Math.max(...named.map((i) => i.name.length)) : 0;
  const descW = Math.max(...items.map((i) => i.descriptor.length));
  return items.map((i) => {
    const namePart = i.name !== "" ? `${padEnd(i.name, nameW)}   ` : "";
    return `${namePart}${padEnd(i.descriptor, descW)}  ${i.total}`;
  });
}

/**
 * A receipt row still awaiting {@link padReceipt}'s final alignment pass — a DAMAGE/HEAT row's
 * unpadded content plus its `label`/`sub` and the `groupId` of the contiguous damage BLOCK it
 * belongs to (never padded across two different blocks, even when nothing separates them in the
 * receipt — a HEAT block immediately followed by another DAMAGE block, with no ARMOR row between
 * them, must NOT have its columns mixed with the next block's).
 */
type PendingDamageRow = { pending: DamagePartsContent; label: string; sub: boolean; groupId: number };

/** The union {@link narrateResolution} builds internally, before {@link padReceipt} resolves every {@link PendingDamageRow} into a final {@link ReceiptRow}. */
type RowSpec = ReceiptRow | PendingDamageRow;

function isPending(row: RowSpec): row is PendingDamageRow {
  return "pending" in row;
}

/**
 * The final padding pass (F, resolution-narration 1.4/1.5 close-out) — see {@link
 * composeDamageBlock}'s doc comment for why content and padding are split. Groups CONSECUTIVE
 * {@link PendingDamageRow}s sharing the same `groupId` (a damage block) and calls
 * `composeDamageBlock` once per group; every other row (TO HIT, the accuracy sub-row, ARMOR,
 * RESIST, IMMUNE, RESULT) passes through untouched — their own spacing is fixed-literal, computed
 * once per row, never cross-row, so there is nothing for this pass to do to them. Grouping by
 * `groupId` (not mere adjacency) is load-bearing: a HEAT block immediately followed by another
 * damage block — nothing intervenes, since HEAT skips the ARMOR/RESIST/IMMUNE rows entirely —
 * would otherwise read as one contiguous run of pending rows and get its columns wrongly mixed
 * with the next block's.
 */
function padReceipt(rows: readonly RowSpec[]): ReceiptRow[] {
  const out: ReceiptRow[] = [];
  let group: PendingDamageRow[] = [];
  const flushGroup = () => {
    if (group.length === 0) return;
    const lines = composeDamageBlock(group.map((g) => g.pending));
    lines.forEach((text, i) => {
      const { pending, label, sub } = group[i]!;
      // The structured cells (2.2) come off the SAME unpadded content the padding was computed
      // from — `total` is `= N`, so its number is the running value.
      const detail = pending.name !== "" ? `${pending.name} ${pending.descriptor}` : pending.descriptor;
      out.push({ label, text, sub, value: pending.total.replace(/^= /, ""), detail });
    });
    group = [];
  };
  for (const row of rows) {
    if (isPending(row)) {
      if (group.length > 0 && group[0]!.groupId !== row.groupId) flushGroup();
      group.push(row);
    } else {
      flushGroup();
      out.push(row);
    }
  }
  flushGroup();
  return out;
}

/**
 * ARMOR row for one NAMED damage step: `ignored (AP)` when the weapon was armor-piercing (armor
 * skipped entirely — no target/armor stat named, matching the spec's AP example verbatim);
 * `<target> armor <N>` alone when armor is `0` (nothing to subtract, no arrow); `<target> armor
 * <N>      <weapon> <preMitigation> → <final>` when armor actually reduced it. `hasRider` appends
 * `(bonus damage not reduced)` — a rider damage step in the SAME resolution bypasses armor
 * entirely (`interpretExtraDamage` hardcodes `armor: 0`), so a player reading the ARMOR row must
 * be told the rider's own total isn't part of what it reduced.
 */
function armorRow(step: DamageStep & { source: NonNullable<DomainDamageStep["source"]> }, targetName: string, hasRider: boolean): ReceiptRow {
  if (step.ap) return { label: "ARMOR", text: "ignored (AP)" };
  let text = `${targetName} armor ${step.armor}`;
  if (step.armor > 0) text += `      ${step.source.name} ${step.preMitigation} → ${step.final}`;
  if (hasRider) text += "  (bonus damage not reduced)";
  if (step.armor <= 0) return { label: "ARMOR", text };
  const riderNote = hasRider ? " (bonus damage not reduced)" : "";
  return { label: "ARMOR", text, value: `${step.final}`, detail: `${targetName} armor ${step.armor}, ${step.source.name} ${step.preMitigation} →${riderNote}` };
}

/** RESIST row: `<target> resists <dtype>   <preArmor> → <final>` — the value that ENTERED resistance is `preMitigation − armor` (0 when AP skipped armor), read straight off the record. */
function resistRow(step: DamageStep, targetName: string): ReceiptRow {
  const beforeResist = step.preMitigation - step.armor;
  const row: ReceiptRow = { label: "RESIST", text: `${targetName} resists ${step.dtype}   ${beforeResist} → ${step.final}`, value: `${step.final}`, detail: `${targetName} resists ${step.dtype} ${beforeResist} →` };
  return beforeResist === step.final ? { ...row, noChange: true } : row;
}

/**
 * IMMUNE row: `<target> is immune to <dtype>   no damage`. **No granting source is named** —
 * `DamageStep.immune` is a bare boolean (see its doc comment in `steps.ts`), so unlike a rider's
 * `source.name` there is nothing recorded to attribute this to. The spec's own worked example
 * names "(Stay of Execution)"; that fact is not on the record, so this narrator cannot reproduce
 * it — reported as a finding rather than invented.
 */
function immuneRow(step: DamageStep, targetName: string): ReceiptRow {
  return { label: "IMMUNE", text: `${targetName} is immune to ${step.dtype}   no damage` };
}

/**
 * `<target> takes <N> damage.` — see `steps.ts`'s `HpDeltaStep` doc comment for why this never
 * goes further ("HP a → b"): no site can safely record post-apply HP when 2+ `hp_delta` land on
 * the same target in one resolution. `N` is the safe, directly-recorded `delta` magnitude, summed
 * across every `hp_delta` this resolution logged against the target — never a re-derivation.
 *
 * **The exact text — `takes N damage.`, with the word "damage" and the terminal period — is a
 * developer ruling (resolution-narration 1.4/1.5 close-out), shown both variants rendered
 * (this one vs. a post-apply-HP form, `HP 20 → 4`) and chosen deliberately over the richer one:
 * ship the honest narrow version, read it on a real station, ask for the richer one if wanted.**
 * This is not a placeholder for the HP half — there is no commented-out or half-built HP variant
 * anywhere in this module; that follow-up is tracked in `projects/resolution-narration.md`
 * (a filed Phase 4 item) and, if it comes back, is a row-TEXT edit here, not a re-shape — keep this
 * function's single string-building shape so a second clause stays a one-line addition.
 */
function resultRow(target: CombatantId, hpDeltaSteps: readonly HpDeltaStep[], names: NameOf): ReceiptRow | undefined {
  const taken = hpDeltaSteps.filter((s) => s.target === target && s.delta < 0).reduce((sum, s) => sum - s.delta, 0);
  if (taken <= 0) return undefined;
  return { label: "RESULT", text: `${names(target)} takes ${taken} damage.` };
}

// ── the consequence rows (resolution-narration 2.5, `decisions/0177`) ──────────────────────

/**
 * A status instance's recorded {@link StatusExpiry}, as the parenthetical the STATUS row trails.
 * `undefined` for `until_removed` (and for an absent expiry, which playback treats as the same
 * thing) — an open-ended status says nothing rather than "(until removed)", which is noise on
 * every GM-applied chip.
 *
 * **The `turn` kind resolves its party to a real NAME off the record.** `StatusExpiry.who` is a
 * ROLE (`"target"` | `"source"`), not an id — the retired ledger passed it straight to `nameOf`
 * and therefore printed the literal word "target". The step itself carries both parties
 * ({@link StatusAddStep.target} is the carrier, `source` is whoever applied it), so this names
 * them instead. `source` is optional (a GM-applied status omits it); with `who: "source"` and no
 * recorded source there is nothing to name, so the clause degrades to the bare edge rather than
 * inventing an applier.
 */
function expiryClause(step: StatusAddStep, names: NameOf): string | undefined {
  const e = step.expiry;
  if (e === undefined || e.kind === "until_removed") return undefined;
  if (e.kind === "end_of_round") return "until the end of the round";
  if (e.kind === "scene") return "for the rest of the scene";
  if (e.kind === "save_ends") return "save ends";
  if (e.kind === "movement") return "for this movement";
  if (e.kind === "this_turn") return "until the end of this turn";
  if (e.kind === "while_status") return `while it has ${e.status}`;
  const who = e.who === "target" ? step.target : e.who === "partner" ? step.partner : step.source;
  if (who === undefined) return `until the ${e.edge} of the applier's next turn`;
  // The holder's own turn reads `its`: the row already opens on the holder's name.
  return who === step.target ? `until the ${e.edge} of its next turn` : `until the ${e.edge} of ${names(who)}'s next turn`;
}

/**
 * `LOADING` — a Loading weapon marking itself unloaded on the shot that just fired it
 * (`resolve/weaponConsume.ts`'s `loadingConsumeStep`, RAW Core p.105). The recorded fact is a
 * `resource_set` on the ATTACKER whose key is `loaded:<weapon instance key>` and whose `val` is
 * `0`; absence of that key means loaded, so `val: 1` is the reload direction (Stabilize's
 * secondary, `stabilize.ts`).
 *
 * **The weapon is named off `about.weapon.name` when the breadcrumb carries one, and NOT
 * reconstructed from the resource key.** The key holds a weapon INSTANCE key (`manual:0`,
 * `0:1`, `npc:2`) — a mount address, not a name, and titling it would print "Manual 0". A
 * hostile NPC's attack has no `about.weapon` at all (the weapon redaction at
 * `EncounterRoom.broadcastResolution`, `decisions/0081`), so that case says `<attacker>'s
 * weapon` — a possessive common noun, deliberately not the bare literal word "weapon" that
 * `resolution-narration` 1.8 removed from the accuracy row, where it was standing in for a
 * source NAME.
 */
function loadingRow(step: ResourceSetStep, about: ResolutionAbout | undefined, names: NameOf): ReceiptRow {
  const subject = about?.weapon ? about.weapon.name : `${names(step.target)}'s weapon`;
  return { label: "LOADING", text: step.val === 0 ? `${subject} is now unloaded — it must be reloaded before it fires again.` : `${subject} is loaded.` };
}

/**
 * One trailing consequence row — what the shot spent, moved, or landed. Returns `undefined` for
 * a step this module deliberately does not surface (see {@link NARRATED_OPS}'s note on
 * `resource_delta`, which stays admitted-but-silent).
 *
 * The four shapes, each read straight off the record and nothing else:
 *  - `status_remove` → `STATUS   <target> loses <Status>`. The canonical emitter is the Lock On
 *    consume (`consumeStatusCosts`), which is the single most common accuracy source in the
 *    game and the reason `decisions/0177` was written; a Hidden attacker revealing itself and a
 *    burned `consumeOnAttack` GM ruling emit the same shape. The row says only that the status
 *    went away, never WHY — the record carries no cause, and "spent" would be a guess on two of
 *    those three.
 *  - `status_add` → `STATUS   <target> gains <Status>[ (<expiry>)]`.
 *  - `heat_delta` → `HEAT   <target> heat <±N>`. See below.
 *  - `resource_set` → the `LOADING` row for a `loaded:` key ({@link loadingRow}); otherwise the
 *    generic `RESOURCE   <target> <key> → <val>`, which is byte-for-byte what the OLD ledger
 *    printed for the same step. That fallback is deliberate rather than a decline: the key/value
 *    pair is exactly what the record holds, so the row is terse but cannot be WRONG — and a
 *    `resource_set` whose key this module doesn't recognise is not a reason to refuse the whole
 *    resolution and lose the rows around it.
 *
 * **`HEAT` is the same label the DAMAGE block uses for a heat-typed `DamageStep`, and the two
 * genuinely are one fact seen twice — the roll, then the track movement — exactly like DAMAGE
 * and RESULT.** They are not a double-report: heat-typed damage routes through
 * `resolveHeatCascade` (`tables.ts`), which CLAMPS to `maxHeat`, so a target already near its
 * cap shows a smaller number on the track row than on the damage row, and that difference is
 * the information. The rows are also structurally distinct — a damage-block HEAT row names the
 * weapon/source and its dice, a track row names a combatant and a signed delta. The common
 * `heat_delta` in a weapon attack has no damage row beside it at all: it is `tg_heat_self` heat
 * on the WIELDER (every shot) or Overkill's reroll heat on the wielder (only on shots where a
 * damage die came up 1). Overkill's own rerolled face is what the record keeps, so the row can
 * say the total and never split it into "1 self-heat + 1 Overkill" — that provenance is not
 * recorded, and inventing it is the thing `decisions/0177` warns about.
 */
function consequenceRow(step: ResolvedStep, about: ResolutionAbout | undefined, names: NameOf): ReceiptRow | undefined {
  switch (step.op) {
    case "status_add": {
      const clause = expiryClause(step as StatusAddStep, names);
      return { label: "STATUS", text: `${names(step.target)} gains ${step.status}${clause === undefined ? "" : ` (${clause})`}` };
    }
    case "status_remove":
      return { label: "STATUS", text: `${names((step as StatusRemoveStep).target)} loses ${step.status}` };
    case "heat_delta":
      return { label: "HEAT", text: `${names((step as HeatDeltaStep).target)} heat ${signedTight(step.delta)}` };
    case "resource_set": {
      const set = step as ResourceSetStep;
      if (set.key.startsWith(LOADED_KEY_PREFIX)) return loadingRow(set, about, names);
      return { label: "RESOURCE", text: `${names(set.target)} ${set.key} → ${set.val}` };
    }
    default:
      return undefined;
  }
}

// ── SAVE (resolution-narration 3.1, `decisions/0177`) ─────────────────────────────────────

/**
 * The recorded save stat as the row's own word — `"hull"` → `"Hull"`. Capitalising the initial is
 * the whole transform: {@link SaveStep.against} is a free string the atom author wrote
 * (`"hull"`/`"agility"`/`"systems"`/`"engineering"`), NOT an id, so {@link displaySourceName} must
 * never touch it (it splits on `[:_-]` and would mangle any hyphenated stat a future atom names).
 */
function saveStatWord(against: string): string {
  return against.length === 0 ? against : `${against[0]!.toUpperCase()}${against.slice(1)}`;
}

/**
 * `SAVE` — one forced save, rolled or skipped, in the same clause grammar as {@link toHitRow}:
 * `<roller> <Stat> save   d20 N[  + B bonus][  ± A Accuracy|Difficulty die]   = total
 * beats|meets|short of DC N — PASS|FAIL`.
 *
 * **The roller is NAMED on the row, unlike the TO HIT row's attacker.** The story line's subject
 * is the ATTACKER; a forced save is rolled by the target, so leaving it unnamed would read as the
 * attacker's own roll. On an area attack the trailing block can also hold several saves by
 * different rollers (see {@link narrateResolution}'s trailing walk), and each naming its own
 * subject is what makes one trailing block correct there.
 *
 * **The verdict word is on the row, not on the story line** — the story sentence describes the
 * ATTACK's outcome, and a shot can hit while its rider's save passes. `emphasis` carries the same
 * verdict for a renderer that colours ({@link ReceiptRow.emphasis}).
 *
 * **`conceded` and `autoFailed` print WHY there is no die, never a `d20 0`.** Both record
 * `d20: 0` and `total: bonus` as a placeholder for a draw that never happened
 * (`effects/interpret.ts`'s two early-return branches — measured off real captures, a
 * `--choice concede_save:<id>=concede` run and a Stunned target's HULL save), so rendering the
 * ordinary clause list would print a die face nobody rolled and a total nobody compared. The row
 * says the skip instead and keeps the `DC N` clause, with no verb because no total was compared;
 * the DC is the one number still true of the save that was forced. **`autoFailed`'s cause is deliberately unnamed** — the step records the
 * skip, not the Stunned status behind it (RAW Core p.78), and naming it would be a compendium
 * re-derivation at render time, the one thing this module may never do.
 *
 * The developer's "a player must never wonder whether X fired" rule is the point of the whole row:
 * a save that PASSED lands nothing, so without this the receipt would show no trace that a save
 * was forced at all — which is exactly the 1.6 content-drop incident this file's header records.
 */
function saveRow(step: SaveStep, names: NameOf): ReceiptRow {
  // A re-save whose status carries no stat records the placeholder `"save_ends"` (`rollSave`,
  // `saves.ts`); it names no stat, so the row does not print one.
  const head = step.against === "save_ends" ? `${names(step.actor)} save` : `${names(step.actor)} ${saveStatWord(step.against)} save`;
  const verdict = step.result === "pass" ? "PASS" : "FAIL";
  if (step.conceded === true || step.autoFailed === true) {
    const why = step.conceded === true ? "conceded, no roll" : "auto-failed, no roll";
    return { label: "SAVE", text: `${head}   ${why}   DC ${step.dc} — ${verdict}`, emphasis: step.result };
  }
  const dcClause = `${rollVerdict(step.total, step.dc, step.result)} DC ${step.dc} — ${verdict}`;
  const parts = [`d20 ${step.d20}`];
  if (step.bonus !== 0) parts.push(`${signedSpaced(step.bonus)} bonus`);
  if (step.accuracy !== 0) parts.push(`${signedSpaced(step.accuracy)} ${step.accuracy > 0 ? "Accuracy" : "Difficulty"} die`);
  return {
    label: "SAVE",
    text: `${head}   ${parts.join("  ")}   = ${step.total}   ${dcClause}`,
    emphasis: step.result,
    value: `${step.total}`,
    detail: `${head} ${parts.join(" ")} ${dcClause}`,
  };
}

/**
 * The save's accuracy/difficulty provenance sub-row — `N Accuracy|Difficulty net:  source ±v · …`,
 * the save axis of the row {@link accuracyProvenanceRow} writes for the attack axis, so the two
 * read identically and a player learns one grammar.
 *
 * **The net is the SUM of {@link SaveStep.accuracyContributions}, and that is the record's own
 * contract rather than this module's arithmetic.** Unlike the to-hit arm there is no
 * `statusAccuracy` aggregate to read: `SaveStep`'s own doc comment states that a save's net count
 * *is* the fold's `saveCheckBonus`, so these entries are that total by construction. The sibling
 * {@link SaveStep.accuracy} is a different number — the rolled d6 MAGNITUDE those counts drew,
 * which {@link saveRow} prints as its `± N Accuracy|Difficulty die` clause. Summing this list does
 * not reproduce it and is not meant to (the spec's "counts and dice are different words" rule).
 * `expectSaveRowBalances` in the golden test pins the sum.
 *
 * Omitted entirely when nothing folded — an unmodified save records no `accuracyContributions` key
 * at all, so there is no `0 net` noise to suppress the way the to-hit arm has to.
 *
 * A contribution whose `carrier` is not the roller was **conferred** by an ally (t_bonded:1's
 * Bondmate on the save axis) and is credited `via <ally>`. A save has ONE party, which is the only
 * thing this differs from the to-hit arm on — the same asymmetry `ResultCard.tsx`'s shared
 * `contributionParenthetical` takes as its `ownIds` parameter.
 */
function saveProvenanceRow(step: SaveStep, names: NameOf): ReceiptRow | undefined {
  const contributions = step.accuracyContributions ?? [];
  if (contributions.length === 0) return undefined;
  const net = contributions.reduce((sum, c) => sum + c.value, 0);
  const bits = contributions.map((c) => `${displaySourceName(c.name)} ${signedTight(c.value)}${c.carrier === step.actor ? "" : ` via ${names(c.carrier)}`}`);
  return { label: "", text: `${Math.abs(net)} ${net >= 0 ? "Accuracy" : "Difficulty"} net:  ${bits.join(" · ")}`, sub: true, accuracy: { net, sources: bits } };
}

// ── the CASCADE rows (resolution-narration 3.2 + 3.3b, `decisions/0177`) ───────────────────
//
// The structure/stress cascade, admitted as ONE narrative unit: a hit that drops a mech to 0 HP
// marks a structure point, offers the structure-loss INTERCEPTION window
// (`structureIntercept.ts` — Custom Paint Job), and then rolls the Structure Damage table, whose
// row destroys an item or the mech itself; heat over the cap does the same through the
// Overheating table. Every row below reads one recorded step and nothing else.
//
// **What these rows deliberately do NOT say: an absolute track value.** `spec.md`'s Custom Paint
// Job example prints `structure 3 → 4,  HP 0 → 1`, and no step records a post-apply figure — the
// same wall `HpDeltaStep`'s own doc comment documents for the RESULT row (a naive per-site
// `after` is provably wrong whenever two deltas land on the same target in one resolution). So
// the deltas are narrated as deltas, which is what the record carries.

/** The one word for a track — used by both the `structure_delta`/`stress_delta` row and its table row, so the two cannot drift apart. */
function trackWord(op: "structure_delta" | "stress_delta"): "structure" | "stress" {
  return op === "structure_delta" ? "structure" : "stress";
}

/**
 * `STRUCTURE` / `STRESS` — one point of the track taken, or given back.
 *
 * **Both directions print, and the "given back" direction is not decoration.** A successful
 * interception emits a compensating `structure_delta +1` *beside* the `-1` it is un-taking
 * (`tables.ts`'s `takeOneStructurePoint`: "the compensating `structure_delta` is a real step, not
 * a rewrite of the one already pushed — the step list is the ledger"), so a receipt that rendered
 * only the loss would show a mech losing a structure point it kept.
 */
function trackDeltaRow(step: StructureDeltaStep | StressDeltaStep, names: NameOf): ReceiptRow {
  const word = trackWord(step.op);
  const label = word === "structure" ? "STRUCTURE" : "STRESS";
  const n = Math.abs(step.delta);
  const text = step.delta < 0 ? `${names(step.target)} takes ${n} ${word} damage.` : `${names(step.target)} gets ${n} ${word} back.`;
  return { label, text };
}

/**
 * `STRUCTURE` / `STRESS` — the **Structure Damage** or **Overheating** table check
 * ({@link TableCheckStep}): the 1d6-per-marked-point pool, the lowest face the row was read from (named only for a pool of two or more),
 * the remaining track the branch consulted, and the row it resolved to.
 *
 * **The number of 1s is named only when it is ≥ 2**, because that is the only time it changes the
 * answer (the catastrophic multiple-1s row — Crushing Hit / Irreversible Meltdown). Printing
 * "0 ones" on every ordinary check would be noise, and printing "1 one" would imply a rule that
 * does not exist.
 *
 * **The row's consequences are NOT restated here.** Each one is its own recorded step with its own
 * row (`status_add` → `STATUS`, `destroy_item` → `DESTROYED`, the Direct Hit HULL check → `SAVE`),
 * which is what keeps this row incapable of contradicting them.
 */
function tableCheckRow(step: TableCheckStep, names: NameOf): ReceiptRow {
  const structure = step.kind === "structure_damage";
  const faces = step.dice.map((d) => `[${d}]`).join(" ");
  // One die is its own lowest face, so only a pool names it.
  const bits = step.dice.length > 1 ? [`lowest ${step.lowest}`] : [];
  if (step.ones >= 2) bits.push(`${step.ones} ones`);
  if (step.track !== undefined) bits.push(`${step.track} ${structure ? "structure" : "stress"} left`);
  const table = step.table ?? (structure ? "Structure Damage" : "Overheating");
  return {
    label: structure ? "STRUCTURE" : "STRESS",
    text: [`${names(step.target)} — ${table}`, `${step.dice.length}d6 ${faces}`, ...(bits.length > 0 ? [bits.join(", ")] : []), `→ ${step.row}`].join("   "),
  };
}

/**
 * `CASCADE` — the AI-tag cascade check (RAW Core p.71) that rides every structure/overheating
 * check made by a mech carrying an active `tg_ai` system ({@link AiCascadeCheckStep}).
 *
 * **The threshold is not restated, deliberately.** The step records the face and an explicit
 * `cascaded` verdict; writing "on a 1" into player-facing copy would be a rule claim this module
 * would then own, which is the drift `frontend/src/rulesCopyRegistry.ts` exists to catch. The
 * verdict is the information; the face is the evidence.
 */
function cascadeCheckRow(step: AiCascadeCheckStep, names: NameOf): ReceiptRow {
  const verdict = step.cascaded ? "the NHP cascades." : "no cascade.";
  return { label: "CASCADE", text: `${names(step.target)} AI cascade check   d20 ${step.d20}   — ${verdict}` };
}

/** `DESTROYED` — the whole combatant ({@link DestroyStep}). `vaporized` is a reactor meltdown's RAW-distinct kill (Core p.81, "the wreck is annihilated"), recorded on the step itself. */
function destroyRow(step: DestroyStep, names: NameOf): ReceiptRow {
  if (!step.destroyed) return { label: "DESTROYED", text: `${names(step.target)} is no longer destroyed.` };
  const tail = step.vaporized === true ? " — vaporized, no wreck." : ".";
  return { label: "DESTROYED", text: `${names(step.target)} is destroyed${tail}` };
}

/**
 * `DESTROYED` — one frozen item (Core p.80 System Trauma), {@link DestroyItemStep}.
 *
 * **The item is named by its KIND and its recorded key, never by a display name — because there
 * is no display name on the record.** `DestroyItemStep.key` is an identity key in the shared
 * destroyed-item namespace: a system's frozen `instanceId`, minted as `system:<i>`
 * (`spawn/loadoutFreeze.ts`), or a weapon's {@link import("@lancer-vtt/domain").weaponInstanceKey}
 * (`<mount>:<slot>` / `integrated:<i>` / `manual:<i>` / `npc:<i>`). Reading the `system:` prefix is
 * a structural read of the record's own documented namespace — the same move {@link loadingRow}
 * makes on `loaded:` — not a compendium lookup. The item's real NAME does reach the player: the
 * cascade emits an advisory warning that carries it (`System Trauma: system "Type-I Flight System"
 * on Fire Horse destroyed.`), and both surfaces print warnings beside the receipt. Same posture as
 * {@link missChanceRow}, which leaves Invisible unnamed for the same reason.
 */
function destroyItemRow(step: DestroyItemStep, names: NameOf): ReceiptRow {
  const kind = step.key.startsWith("system:") ? "system" : "weapon";
  const verb = step.destroyed ? "loses" : "recovers";
  return { label: "DESTROYED", text: `${names(step.target)} ${verb} a ${kind} (${step.key}).` };
}

/**
 * `PAINT JOB` — the structure-loss **interception window**'s record-only witness
 * ({@link InterceptStep}, `resolution-narration` 3.3a), and the row the developer's own completion
 * bar for this project names. All three branches, verbatim from
 * `projects/resolution-narration/spec.md`:
 *
 * ```
 * PAINT JOB Custom Paint Job   charge spent, d6 rolls 6 (needs 6)   → hit ignored
 * PAINT JOB Custom Paint Job   charge spent, d6 rolls 3 (needs 6)   → no effect, structure damage stands
 * PAINT JOB Custom Paint Job   offered, declined
 * ```
 *
 * **`charge spent` is read off a paired step, never assumed.** The offer's charge is a real
 * `affordance_use_set` in the same list ({@link chargeSpentFor}); when one is paired to this
 * witness the clause prints and that step emits no second row of its own, which is how the spec's
 * single legible sentence stays one row without double-reporting the spend. An offer that somehow
 * fired with no paired charge step says so by omission rather than by claiming a spend the record
 * does not carry.
 *
 * **The label is `PAINT JOB` because the spec and the developer's completion bar both name that
 * word, and there is exactly one fielded offer** (`STRUCTURE_INTERCEPT_OFFERS` holds only Custom
 * Paint Job; `structureIntercept.ts`'s header records that a second carrier is a recorded target,
 * not a shipped one). The ability's own display name leads the row TEXT, off the recorded
 * `source.name` — a genuine title authored beside the offer, never an id run through
 * {@link displaySourceName} — so if a second offer is ever fielded this label becomes a generic
 * word in a one-line edit and none of the text moves.
 *
 * **The `structure 3 → 4,  HP 0 → 1` second line of the spec's example is NOT rendered**: those
 * are absolute post-apply track values and no step records one. The same facts ride as their own
 * rows off their own recorded steps — the compensating `structure_delta +1` ({@link trackDeltaRow})
 * and the `hp_delta +1` ({@link recoverRow}) — which is the honest form of the same information.
 */
function interceptRow(step: InterceptStep, chargeSpent: boolean): ReceiptRow {
  if (step.declined === true) return { label: "PAINT JOB", text: `${step.source.name}   offered, declined` };
  if (step.roll === undefined) return { label: "PAINT JOB", text: `${step.source.name}   offered` };
  const spend = chargeSpent ? "charge spent, " : "";
  const verdict = step.ignored ? "→ hit ignored" : "→ no effect, structure damage stands";
  return { label: "PAINT JOB", text: `${step.source.name}   ${spend}d${step.sides} rolls ${step.roll} (needs ${step.threshold})   ${verdict}`, emphasis: step.ignored ? "pass" : "fail" };
}

/**
 * `CHARGE` — a consumed-use count moving on the one per-affordance ledger
 * ({@link AffordanceUseSetStep}, `decisions/0176`). The live emitters inside an attack resolution
 * are a **Limited weapon's** consume-on-fire (`resolve/weaponConsume.ts`'s `limitedConsumeStep`,
 * keyed by the weapon's instance key) and the interception window's charge spend (keyed by the
 * granted action's affordance key) — the latter is absorbed by its {@link interceptRow} and never
 * reaches here.
 *
 * **Terse on purpose, in the `RESOURCE` row's mould.** `key` is an identity key
 * (`ms_custom_paint_job:0`, `manual:0`), not a name, and titling it would print "Custom Paint Job
 * 0" — the render-time re-derivation of an id shape that `decisions/0177` forbids. The recorded
 * key and count are exactly what the record holds, so the row is terse but cannot be wrong.
 */
function chargeRow(step: AffordanceUseSetStep, names: NameOf): ReceiptRow {
  return { label: "CHARGE", text: `${names(step.target)} ${step.key} → ${step.uses ?? 0} used` };
}

/**
 * `WAIVED` — a {@link CostWaivedStep}: charges the resolution owed that a replacement clause
 * prevented (Ammo Case III's Efficiency on a first-roll crit). Names the clause as recorded, which
 * the room has already redacted for an undisclosed carrier.
 */
function waivedRow(step: CostWaivedStep, names: NameOf): ReceiptRow {
  return { label: "WAIVED", text: `${names(step.target)} ${step.key}: ${step.amount} charge${step.amount === 1 ? "" : "s"} not expended (${step.by.name}).` };
}

/**
 * `HP` — a POSITIVE {@link HpDeltaStep}. `hp_delta` has been admitted since Phase 1, but only its
 * negative half had a row ({@link resultRow}, which sums the damage taken); a positive one
 * rendered nothing, which was invisible only because every shape that emits one declined whole
 * before 3.2. Two live emitters, both in the cascade: the RAW "your HP resets to full" step a
 * surviving structure check pushes, and a successful interception's restore to 1 HP.
 *
 * Says the delta, never a post-apply total — see this section's header for why.
 */
function recoverRow(step: HpDeltaStep, names: NameOf): ReceiptRow {
  return { label: "HP", text: `${names(step.target)} regains ${step.delta} HP.` };
}

// ── the DISPLACEMENT / BURN rows (resolution-narration 3.4, `decisions/0177`) ──────────────
//
// `move`, `forced_move` and `burn_set` — the group every earlier roadmap line left unscoped, and
// the one holding out every knockback weapon (`mw_concussion_missiles`) and `t_hacker:1`'s
// tech-attack push branch. It is a RENDERER job, not a record job: `MoveStep`/`ForcedMoveStep`
// already carry `target`/`from`/`to`(/`distance`), and `BurnSetStep` carries the absolute mark
// plus its `prev`. Every row below reads one recorded step and nothing else.

/**
 * A recorded {@link Cell} as the receipt's own coordinate phrase — `(3, 0)` for a square cell,
 * `(q, r)` for a hex one. Grid-agnostic over the tagged union exactly like `ResultCard.tsx`'s
 * ledger `cellText`, which has printed the same shape since the card existed; the two agree by
 * choice, not by sharing, because moving the ledger's bytes is not this phase's job.
 */
function cellText(cell: Cell): string {
  if ("grid" in cell && cell.grid === "square") return `(${cell.x}, ${cell.y})`;
  const h = cell as { q: number; r: number };
  return `(${h.q}, ${h.r})`;
}

/**
 * `MOVE` — a voluntary {@link MoveStep}: a combatant's recorded cell change. The live emitters
 * inside a committed resolution are the manual move tier, a mount/dismount (`jockey.ts`), a
 * dragged rider following its mover (`spatialRecompute.ts`) and a destroyed combatant being
 * unplaced (`wreckConversion.ts`, `to: null`).
 *
 * **Both endpoints print when both are recorded**, because a displacement's *from* is as much a
 * recorded fact as its *to* and a reader of a play-by-play wants the pair. `null` on either side
 * is an UNPLACED combatant (the field's own documented meaning), and the row says that rather
 * than inventing a coordinate: an unplaced start reads "is placed at", an unplaced end reads
 * "is removed from the map".
 */
function moveRow(step: MoveStep, names: NameOf): ReceiptRow {
  const who = names(step.target);
  if (step.to === null) return { label: "MOVE", text: `${who} is removed from the map.` };
  if (step.from === null) return { label: "MOVE", text: `${who} is placed at ${cellText(step.to)}.` };
  return { label: "MOVE", text: `${who} moves ${cellText(step.from)} → ${cellText(step.to)}.` };
}

/**
 * `DISPLACED` — an involuntary {@link ForcedMoveStep}: Knockback, a push/pull, or an
 * effect-directed self-reposition.
 *
 * **The verb is the passive "is moved", deliberately NOT "knocked back".** The old ledger says
 * "knocked back N" and that is wrong for two of the three things this op records: `distance`
 * carries two conventions by emitter (`ForcedMoveStep`'s own doc comment), and the emitters are a
 * push AWAY (`TAG_KNOCKBACK`), a pull TOWARD, and `interpretMoveSelfTowardTarget`'s VOLUNTARY
 * self-reposition (Hunter's Lunge, Ace's Acrobatics), which records the distance actually
 * travelled. Nothing on the record distinguishes them, so naming a direction would be a guess —
 * the exact "a wrong row is worse than the fallback it replaced" failure `decisions/0177` forbids.
 * The cells are printed, so a reader can see the direction for themselves.
 *
 * **A `to: null` says the displacement did not land, and does not fake a destination.** The field's
 * own doc comment records that as the answerer failing to place the push (no map, off-map, an
 * unanswerable arc); the intended distance still rides the row because that is what was recorded.
 */
function forcedMoveRow(step: ForcedMoveStep, names: NameOf): ReceiptRow {
  const who = names(step.target);
  const spaces = `${step.distance} space${step.distance === 1 ? "" : "s"}`;
  if (step.teleport && step.from !== null && step.to !== null) return { label: "TELEPORT", text: `${who} teleports ${spaces}, ${cellText(step.from)} → ${cellText(step.to)}.` };
  if (step.to === null) return { label: "DISPLACED", text: `${who} is moved ${spaces} — no destination recorded.` };
  if (step.from === null) return { label: "DISPLACED", text: `${who} is moved ${spaces} → ${cellText(step.to)}.` };
  return { label: "DISPLACED", text: `${who} is moved ${spaces}, ${cellText(step.from)} → ${cellText(step.to)}.` };
}

/**
 * `BURN` — the marked-burn track moving ({@link BurnSetStep}), which is an ABSOLUTE set carrying
 * its own `prev`, so the row can print the movement and the delta without computing anything the
 * record does not hold.
 *
 * **The mark is not the damage, and the row must not read as if it were.** A burn-typed hit emits
 * a `damage` step (its own DAMAGE row) plus an `hp_delta` (the RESULT row) plus this mark — one
 * attack, three recorded facts. Measured off a real Napalm Bomb capture (`cli/proofs/
 * burn-readout.test.ts`'s cast): `damage(burn 4) · hp_delta -4 · burn_set{burn: 4, prev: 0}`. So this
 * row says "is marked for 4 burn", never "takes 4 burn" — the taking is the RESULT row's.
 *
 * The clearing direction is a real emitter too (`resolve/burn.ts`'s turn-start tick, Stabilize's
 * secondary), and it prints for the same "a player must never wonder whether X fired" reason every
 * other consequence row exists.
 */
function burnRow(step: BurnSetStep, names: NameOf): ReceiptRow {
  const who = names(step.target);
  const delta = step.burn - step.prev;
  if (delta > 0) return { label: "BURN", text: `${who} is marked for ${delta} burn — marked burn ${step.prev} → ${step.burn}.` };
  if (delta < 0) return { label: "BURN", text: `${who} clears ${Math.abs(delta)} marked burn — marked burn ${step.prev} → ${step.burn}.` };
  return { label: "BURN", text: `${who} marked burn stays at ${step.burn}.` };
}

// ── the REMAINING recorded consequences (resolution-narration 3.4, `decisions/0177`) ───────
//
// The eleven ops 3.4's roadmap line names. Each is one recorded step and one row, read off the
// record's own fields and nothing else. **Several of them are, on today's content, only ever
// recorded in a resolution with no segment head** — a Grapple break's `contest`, a thrown Smoke
// Charge's `area_add`, a mine detonation's `concealed_set` (all three MEASURED, see
// {@link NARRATED_OPS}' doc comment) — so admitting them does not by itself make those
// resolutions narrate: they decline on the shape gate, which is `resolution-narration` 4.1's.
// They are admitted and rendered anyway, because the ops DO ride an attack the moment a weapon's
// on-hit clause carries one, and a renderer that exists is what makes that day silent rather
// than a fresh decline.

/**
 * `CONTEST` — a two-roll opposed check ({@link ContestStep}), in the same clause grammar as
 * {@link saveRow} so a player learns one vocabulary for every roll the engine records: a verdict
 * row naming both axes and the winner, then one indented sub-row per side naming that side and
 * showing its own draw. The parties are named once, on the sub-rows, to keep the verdict row short
 * at the COMBAT sidebar's width (`narration-ledger-retirement` 6.2).
 *
 * **Two sub-rows rather than one packed line**, because the two draws are independent and each has
 * its own bonus and its own accuracy die; folding them into one row would produce the same
 * "which number belongs to whom" confusion this project exists to end.
 *
 * **`tie` is printed with its consequence, not just as a flag.** {@link ContestStep} records the
 * RAW initiator-wins-ties outcome, so a tie is a challenger win and the row says so.
 *
 * **An auto-failed side prints the skip, never a `d20 0`** — exactly as {@link saveRow} does for a
 * conceded or auto-failed save, and for the same recorded reason (the step records `d20: 0` as a
 * placeholder for a draw that never happened). Its cause is deliberately unnamed: the step records
 * the skip, not the Stunned status behind it.
 *
 * **The axes are printed as recorded LABELS, capitalised only.** `challengerAxis`/`defenderAxis`
 * are documented as labels for the play-by-play, not `base[axis]` reads — Stasis Bolt's
 * Interdiction Point contests a ranged attack roll, not a HASE stat — so {@link displaySourceName}
 * must never touch them (same reasoning as {@link saveStatWord}, whose transform this reuses).
 */
function contestRows(step: ContestStep, names: NameOf): ReceiptRow[] {
  const challenger = names(step.challenger);
  const defender = names(step.defender);
  const winner = step.winner === "challenger" ? challenger : defender;
  const tie = step.tie ? ", tied — the initiator wins" : "";
  const head: ReceiptRow = {
    label: "CONTEST",
    text: `${saveStatWord(step.challengerAxis)} vs ${saveStatWord(step.defenderAxis)} — ${winner} wins${tie}`,
  };
  const side = (who: string, roll: ContestStep["challengerRoll"], autoFailed: boolean | undefined): ReceiptRow => {
    if (autoFailed === true) return { label: "", text: `${who}   auto-failed, no roll`, sub: true };
    const parts = [`d20 ${roll.d20}`];
    if (roll.bonus !== 0) parts.push(`${signedSpaced(roll.bonus)} bonus`);
    if (roll.accuracy !== 0) parts.push(`${signedSpaced(roll.accuracy)} ${roll.accuracy > 0 ? "Accuracy" : "Difficulty"} die`);
    return { label: "", text: `${who}   ${parts.join("  ")}   = ${roll.total}`, sub: true };
  };
  return [head, side(challenger, step.challengerRoll, step.challengerAutoFailed), side(defender, step.defenderRoll, step.defenderAutoFailed)];
}

/**
 * One row for each of the remaining recorded consequences 3.4 admits. Returns `undefined` for a
 * step this walk does not surface, so the caller can chain it behind {@link cascadeRow}.
 *
 * Each row's shape, and the reason it is the shape it is:
 *
 *  - `discharge_set` → `DISCHARGE   <target> <key> → discharged|recharged`. Terse in the
 *    {@link chargeRow}/`RESOURCE` mould, and for the identical reason: `key` is an identity key (a
 *    weapon instance key `npc:0`, or a granted action's affordance key), not a name, and titling
 *    it would print "Npc 0". MEASURED riding a real attack — `cli/proofs/npc-recharge.test.ts`'s
 *    Recharge Cannon records `to_hit · discharge_set{key: "npc:0"} · damage · hp_delta ·
 *    resource_delta`, so this is the one op in 3.4's eleven that a shipped weapon puts on an
 *    ordinary narrated attack today.
 *  - `status_suspend_set` → `SUSPEND   <target> <Status> → expiry suspended|resumed`. Terse
 *    deliberately: the step records that the instance's expiry timer was frozen or resumed, and
 *    nothing about WHY. No shipped content emits it (`rg suspend_status` over `packs/` and
 *    `seedEntries.ts` is empty), so this row's only current job is to keep the op from being a
 *    silent decline the day something does.
 *  - `resource_clear` → the `LOADING` row for a `loaded:` key (clearing that key is the reload
 *    direction — absence means loaded, {@link loadingRow}'s own contract), otherwise
 *    `RESOURCE   <target> <key> cleared`. `restoreVal` is not printed: it is the inverse's
 *    bookkeeping, not a fact about what just happened.
 *  - `mine_armed_set` → `MINE   <target> is armed.|is disarmed.`
 *  - `concealed_set` → `CONCEALMENT   <target> is revealed.|is concealed.`
 *  - `disclosure_set` → `DISCLOSURE   <target> — disclosure <prev> → <level>`, with `unset` for
 *    the genuine "never scanned" absence the field distinguishes from a named level. The three
 *    levels are printed as the recorded enum words (`class`/`loadout`/`full`) rather than being
 *    prosified, because they are the vocabulary the rest of the UI already shows.
 *  - `features_revealed` → `REVEALED   <target> — <n> features revealed by use`. A count, never
 *    the ids: the room applies this step on its own beside a resolution (`decisions/0207`), so the
 *    row only shows in a history replay, and the dossier is where the features themselves read.
 *  - `spawn_object`/`despawn_object` → `DEPLOYED   <name> is deployed[ at (x, y)].` /
 *    `REMOVED   <name> is removed from the field.` **The name comes off the step's own full
 *    {@link Combatant} record, not through `names`** — a freshly spawned combatant is not in the
 *    broadcast's id→name map, so `names` would degrade it to a raw uuid.
 *  - `area_add`/`area_remove` → `AREA   soft cover (disk 2) at (3, 4), from <source> — appears.|
 *    — disperses.` Every field is recorded on {@link AreaEffect} (`grants`, `shape`, `size`,
 *    `origin`, `source`); the `source` is named so this row, like every other, names a subject.
 *    MEASURED head-less on today's content: `cli/proofs/smoke-charges.test.ts` records
 *    `action :: area_add`, a resolution with no `to_hit`.
 */
function residueRow(step: ResolvedStep, about: ResolutionAbout | undefined, names: NameOf): ReceiptRow | undefined {
  switch (step.op) {
    case "move":
      return moveRow(step, names);
    case "move_budget_delta":
      return { label: "MOVE", text: `${names(step.target)} ${step.delta >= 0 ? "gains" : "loses"} ${Math.abs(step.delta)} ${Math.abs(step.delta) === 1 ? "space" : "spaces"} of movement.` };
    case "forced_move":
      return forcedMoveRow(step, names);
    case "burn_set":
      return burnRow(step, names);
    case "discharge_set":
      return { label: "DISCHARGE", text: `${names(step.target)} ${step.key} → ${step.discharged ? "discharged" : "recharged"}` };
    case "status_suspend_set":
      return { label: "SUSPEND", text: `${names(step.target)} ${step.status} → expiry ${step.suspended ? "suspended" : "resumed"}` };
    case "resource_clear":
      return step.key.startsWith(LOADED_KEY_PREFIX)
        ? { label: "LOADING", text: `${about?.weapon ? about.weapon.name : `${names(step.target)}'s weapon`} is loaded.` }
        : { label: "RESOURCE", text: `${names(step.target)} ${step.key} cleared` };
    case "mine_armed_set":
      return { label: "MINE", text: `${names(step.target)} is ${step.armed ? "armed" : "disarmed"}.` };
    case "concealed_set":
      return { label: "CONCEALMENT", text: `${names(step.target)} is ${step.concealed ? "concealed" : "revealed"}.` };
    case "disclosure_set":
      return { label: "DISCLOSURE", text: `${names(step.target)} — disclosure ${step.prev ?? "unset"} → ${step.disclosure ?? "unset"}` };
    case "features_revealed": {
      const n = step.ids?.length ?? 0;
      return { label: "REVEALED", text: `${names(step.target)} — ${n} ${n === 1 ? "feature" : "features"} revealed by use` };
    }
    case "spawn_object": {
      const at = step.combatant.pos ? ` at ${cellText(step.combatant.pos)}` : "";
      return { label: "DEPLOYED", text: `${step.combatant.name} is deployed${at}.` };
    }
    case "despawn_object":
      return { label: "REMOVED", text: `${step.combatant.name} is removed from the field.` };
    case "area_add":
    case "area_remove": {
      const a = step.area;
      const grants = a.grants === "blind_out" ? "blinding light" : a.grants === "none" ? "marked space" : a.grants.replace(/_/g, " ");
      const verb = step.op === "area_add" ? "appears" : "disperses";
      return { label: "AREA", text: `${grants} (${a.shape} ${a.size}) at ${cellText(a.origin)}, from ${names(a.source)} — ${verb}.` };
    }
    default:
      return undefined;
  }
}

/**
 * Pair each {@link InterceptStep} that FIRED with the `affordance_use_set` charge spend it caused,
 * and return the indices of the charge steps so paired — they render inside the `PAINT JOB` row's
 * `charge spent` clause rather than as `CHARGE` rows of their own.
 *
 * **The pairing is structural, off the record's own key namespace, not positional guesswork.** An
 * offer's charge is spent through the granted action's affordance key, which
 * {@link import("@lancer-vtt/domain").affordanceKey} mints as `` `${itemId}:${index}` `` — and the
 * witness records that same `itemId` as `source.id`. So a charge step whose `target` matches the
 * witness's and whose `key` is that item's affordance key is the one this offer spent. A charge
 * step is claimed at most once, and only by a witness that comes AFTER it (the emitter pushes the
 * spend before the die is drawn, `structureIntercept.ts`), so two interceptions in one cascade
 * cannot cross-claim.
 */
function chargeSpentFor(steps: readonly ResolvedStep[]): { charges: ReadonlySet<number>; witnesses: ReadonlySet<number> } {
  const charges = new Set<number>();
  const witnesses = new Set<number>();
  steps.forEach((step, i) => {
    if (step.op !== "intercept" || step.roll === undefined) return;
    for (let j = i - 1; j >= 0; j--) {
      const c = steps[j]!;
      if (c.op !== "affordance_use_set" || charges.has(j)) continue;
      if (c.target !== step.target) continue;
      if (c.key !== step.source.id && !c.key.startsWith(`${step.source.id}:`)) continue;
      charges.add(j);
      witnesses.add(i);
      return;
    }
  });
  return { charges, witnesses };
}

/**
 * One cascade row, or `undefined` for a step this walk does not surface. `paired` is
 * {@link chargeSpentFor}'s verdict: a charge step it claimed renders nothing here (its fact is
 * already inside the `PAINT JOB` row), and a witness it matched prints the `charge spent` clause.
 */
function cascadeRow(args: { step: ResolvedStep; names: NameOf; paired: { charges: ReadonlySet<number>; witnesses: ReadonlySet<number> }; index: number }): ReceiptRow | undefined {
  const { step, names, paired, index } = args;
  switch (step.op) {
    case "structure_delta":
    case "stress_delta":
      return trackDeltaRow(step, names);
    case "table_check":
      return tableCheckRow(step, names);
    case "ai_cascade_check":
      return cascadeCheckRow(step, names);
    case "destroy":
      return destroyRow(step, names);
    case "destroy_item":
      return destroyItemRow(step, names);
    case "intercept":
      return interceptRow(step, paired.witnesses.has(index));
    case "affordance_use_set":
      return paired.charges.has(index) ? undefined : chargeRow(step, names);
    case "cost_waived":
      return waivedRow(step, names);
    case "hp_delta":
      return step.delta > 0 ? recoverRow(step, names) : undefined;
    default:
      return undefined;
  }
}

/**
 * `MISS CHANCE` — the Invisible pre-roll coin (RAW Core p.77; `resolve/attackPreRoll.ts`'s
 * `rollPreRollMissChance`), which is a whole per-target outcome rather than a consequence: the
 * attack missed OUTRIGHT, before a d20 was ever rolled, so this target's segment has no TO HIT
 * row, no damage, and no RESULT.
 *
 * **The granting status is NOT named, deliberately.** Invisible is the only shipped source of a
 * `missChance` facet today, but {@link MissChanceStep} records only `attacker`/`target`/`coin`/
 * `chance` — naming "Invisible" would be re-deriving a fact off the compendium at render time,
 * which is the one thing this module may never do. The recorded probability is said instead,
 * which is the honest version of the same information; the resolver's own advisory warning
 * ("target is Invisible — …") still prints beside the receipt on both surfaces and does name it.
 */
function missChanceRow(step: MissChanceStep, names: NameOf): ReceiptRow {
  const pct = Math.round(step.chance * 100);
  return { label: "MISS CHANCE", text: `${names(step.target)} — ${pct}% miss chance: the coin came up a miss, so the shot goes wide before the d20.`, emphasis: "miss" };
}

// ── the Barrage framing rows (resolution-narration 2.2) ────────────────────────────────────

/**
 * The **shot-framing rows** a Barrage shot and an Auxiliary rider open their receipt with — the
 * nearest thing the record supports to the roadmap's "bundle sentence, then each sub-attack's
 * paragraph" (`resolution-narration` 2.2).
 *
 * **Why it is a row and not a bundle.** Measured off a real three-shot Barrage's `feed.jsonl`
 * (2026-09-06): since `barrage-sequential-resolution` (`decisions/0049`) each Barrage shot is its
 * OWN independently-committed resolution — its own `apply_resolved`, its own feed entry, its own
 * single `to_hit`. Two shots never share a step list, so there is no bundle for one narrated card
 * to sentence over; a card can only say that the shot it renders belonged to a Barrage. Anything
 * about the chain as a whole (how many shots fired, which one this is, whether it is still open)
 * is live lock state, not record — the CLI's `barrage` verb prints it from
 * `activeAttack(res.state.activation)`, which the narrator by rule cannot read.
 *
 * **Why it is a row and not a clause on the story line.** The story sentence's verb ("fires the
 * X at Y") is the subject of an OPEN question with the developer (should Ram/Grapple/an
 * improvised attack read differently — `projects/resolution-narration.md`'s station question 2);
 * bending it for Barrage would pre-empt his answer. The receipt is where this project puts a
 * named fact, one per row.
 *
 * Both facts are independent, and both are omitted when absent: an ordinary Skirmish gets neither
 * row, a Barrage's primary/continuation shot gets `BARRAGE` alone, a SKIRMISH Aux rider (Core
 * p.70) gets `AUX RIDER` alone, and a Barrage's Aux rider (p.71) gets both. A Main fired after its
 * mount's Aux (`about.auxRider`) is the primary; its `AUX RIDER` row names that earlier Aux as the
 * rider instead of this shot.
 *
 * `AUX RIDER`'s text is deliberately the bare rule clause ("no bonus damage") and not an
 * explanation of the eligibility rule that produced it — the eligibility wall lives in
 * `economy/barrage.ts`'s `isRiderShot`, and restating it in player-facing copy is exactly
 * the class of drift `frontend/src/rulesCopyRegistry.ts` exists to catch.
 */
function chainRows(about: ResolutionAbout | undefined): ReceiptRow[] {
  const rows: ReceiptRow[] = [];
  if (about?.reaction === "overwatch") rows.push({ label: "OVERWATCH", text: "reaction" });
  if (about?.reaction === "covering_fire") rows.push({ label: "COVERING FIRE", text: "reaction, half damage" });
  // A shot another rule grants (Impending Threat, Moving Target) is labelled by its feature, `about.item`.
  if (about?.reaction !== undefined && about.reaction !== "overwatch" && about.reaction !== "covering_fire") rows.push({ label: (about.item?.name ?? "reaction").toUpperCase(), text: "reaction" });
  if (about?.barrage) rows.push({ label: "BARRAGE", text: "one shot of the Barrage" });
  if (about?.noBonus) rows.push({ label: "AUX RIDER", text: "no bonus damage" });
  if (about?.auxRider) rows.push({ label: "AUX RIDER", text: `${about.auxRider.weapon?.name ?? "the Aux fired earlier"}, no bonus damage` });
  return rows;
}

// ── the story line ─────────────────────────────────────────────────────────────────────────

/** `HIT` / `CRITICAL HIT` / `MISS` — the one outcome vocabulary, shared by the single-target story line and every per-target clause of an area one. */
function outcomeWord(result: ToHitStep["result"]): string {
  return result === "crit" ? "CRITICAL HIT" : result === "hit" ? "HIT" : "MISS";
}

/**
 * One segment's outcome. A `miss_chance`-headed segment is always a `"miss"` — the coin's losing
 * face is the outright miss itself (`resolve/attackPreRoll.ts` early-outs before the d20), which
 * is a genuinely different event from a rolled miss but the same word to a reader; the receipt's
 * `MISS CHANCE` row is what tells the two apart.
 */
function headOutcome(head: SegmentHead): ToHitStep["result"] {
  return head.op === "to_hit" ? head.result : "miss";
}

/** The HP damage one segment actually dealt: every non-heat, non-immune `DamageStep.final` in it. Heat is never damage; an immune hit dealt none by definition. */
function damageDealt(damageSteps: readonly DamageStep[]): number {
  return damageSteps.filter((s) => s.dtype !== "heat" && !s.immune).reduce((sum, s) => sum + s.final, 0);
}

/**
 * ` (Blast 2)` — the fired weapon's area template, off the RECORDED `about.weapon.area`
 * (`resolution-narration` 2.1; see `ResolutionAbout`'s doc comment in `steps.ts` for why the shape
 * is recorded rather than re-derived here). Empty string for an ordinary single-target weapon, and
 * for any entry persisted before that field existed — the story line simply omits the clause
 * rather than guessing a shape. A `size` of `0` prints the bare shape word.
 *
 * **Placed right after the weapon name** (`fires the Mortar (Blast 1) at Goblin-3`), not after the
 * target as `projects/resolution-narration/spec.md`'s own area example shows it. The spec put it
 * after an ORIGIN phrase ("at the square east of the ridge") that nothing on the record can supply
 * — an area attack's aimed cell is not on `about`, and a cell has no name a player would recognize
 * — so the clause it trailed does not exist here. Attached to the weapon it reads the same in both
 * the cell-aimed case (no target clause at all) and the unit-aimed one.
 */
function areaClause(about: ResolutionAbout | undefined): string {
  const area = about?.weapon?.area;
  if (area === undefined) return "";
  const word = area.kind[0]!.toUpperCase() + area.kind.slice(1);
  return area.size > 0 ? ` (${word} ${area.size})` : ` (${word})`;
}

/** The attack actions whose story line names the action (`rams Def`) rather than a weapon. */
const ACTION_VERBS: Readonly<Record<string, { verb: string; bare: string }>> = {
  ram: { verb: "rams", bare: "rams" },
  grapple: { verb: "grapples", bare: "grapples" },
  improvised_attack: { verb: "makes an improvised attack on", bare: "makes an improvised attack" },
};

/**
 * The story line's verb clause — what the actor DID, up to but not including the outcome. Four
 * shapes, checked in this order:
 *
 *  1. **A TECH ATTACK** (`resolution-narration` 2.3) — `makes a tech attack on <target>`, or
 *     `makes a tech attack` with no target clause for the area form. Recognized off the segment
 *     head's own `to_hit.vs === "edef"`, unless `about` names a weapon ({@link isTechAttack}: a
 *     Smart weapon also attacks E-Defense, and reads as shape 2 or 3); `about.action` is `"quick_tech"` for the INVADE route but is absent on other
 *     paths (a released hold, an NPC feature) and names the action-economy slot rather than the
 *     attack, so it is not what this reads.
 *
 *     **Why a generic verb and not "invades".** The tech attack's `intent.weapon` is an ACTION id
 *     that `selectWeapon` cannot resolve, which is why `aboutWeaponFor` records no weapon for a
 *     tech attack at all (before 2.3 it recorded `{name: "Unarmed strike", id: "__fallback__"}`
 *     and a real INVADE would have narrated as "fires the Unarmed strike at Defender"). The
 *     OPTION is recorded since `comms-attack-reaction-cards` 1.3 (`about.techOption`, Invade only
 *     — a system-granted tech attack's action name is still on nothing the engine holds), and
 *     the COMMS card reads it; this sentence stays generic on purpose, so the CLI and card story
 *     lines read the same for every tech attack. What the attack DID lands in the receipt's HEAT
 *     and STATUS rows, which for a real INVADE read `Defender heat +2` / `Defender gains Impaired
 *     (until the end of its next turn)` / `… gains Slowed (…)`.
 *  2. **A named MELEE weapon** (`comms-attack-reaction-cards` 2.3, off `about.weapon.melee`) —
 *     `attacks[ <target>] with the <weapon>[ (Burst N)]`. "fires" was wrong for a sword, a Ram and
 *     an unarmed strike alike; an entry recorded before `melee` existed still reads "fires".
 *  3. **Any other named weapon** — `fires the <weapon>[ (Blast N)][ at <target>]`.
 *  4. **Neither** — `attacks[ <target>]`, the honest degradation for an old `feed.jsonl` entry or
 *     a resolution whose `about` never carried a weapon.
 */
/**
 * Whether an E-Defense `to_hit` was a tech attack rather than a Smart weapon's attack. Both check
 * E-Defense; only a weapon attack records `about.weapon`. An entry recorded before
 * `resolution-narration` 2.3 carried a bogus weapon on a tech attack, so the Quick Tech slot still
 * reads as tech.
 */
export function isTechAttack(head: { vs: ToHitStep["vs"] }, about: ResolutionAbout | undefined): boolean {
  return head.vs === "edef" && (about?.weapon === undefined || about.action === "quick_tech");
}

function attackClause(head: SegmentHead, about: ResolutionAbout | undefined, targetName: string | undefined): string {
  const at = targetName === undefined ? "" : ` ${targetName}`;
  if (head.op === "to_hit" && isTechAttack(head, about)) return `makes a tech attack${targetName === undefined ? "" : ` on ${targetName}`}`;
  // A Ram, a Grapple or an Improvised Attack names its action, not the synthetic weapon profile it
  // resolves through ("attacks Def with the Grapple"). `about.action` is the ledger id the engine
  // records for each (`economy/actionId.ts`). Developer-approved 2026-09-25
  // (`narration-ledger-retirement`, owed item ^p8jg).
  const actionVerb = about?.action !== undefined ? ACTION_VERBS[about.action] : undefined;
  if (actionVerb !== undefined) return targetName === undefined ? actionVerb.bare : `${actionVerb.verb} ${targetName}`;
  if (about?.weapon?.melee) return `attacks${at} with the ${about.weapon.name}${areaClause(about)}`;
  if (about?.weapon) return `fires the ${about.weapon.name}${areaClause(about)}${targetName === undefined ? "" : ` at ${targetName}`}`;
  return `attacks${at}`;
}

/**
 * The story line's optional **second sentence** — what the cascade did to the story's own target
 * (`resolution-narration` 3.2 + 3.3b). `spec.md`'s Custom Paint Job example is the shape this
 * reproduces: `… — HIT, 9 damage. Custom Paint Job shrugs off the structure loss.`
 *
 * **THE RULE THIS SETTLES, written down because 3.1 deferred it here.** The story line carries
 * the attack's outcome and, when the record shows the TARGET'S STANDING IN THE FIGHT changed, one
 * further clause naming that change — destroyed, a structure point lost, a structure loss shrugged
 * off, a stress point lost. **MECHANISM stays in the receipt**: a save's verdict, a table row, a
 * die face, a charge spent, an accuracy source. That is why 3.1's `SAVE` row keeps its PASS/FAIL
 * on the row rather than on the sentence and needs no edit — a save is how a rider resolved, not a
 * change in what the target still is.
 *
 * Three consequences of the rule worth stating, each of which a narrower version got wrong:
 *
 *  - **It is scoped to the story's own target.** A `tg_heat_self` weapon overheats its WIELDER, so
 *    a real Vulture DMR shot fired at the heat cap records `stress_delta`/`table_check` against the
 *    ATTACKER (captured, not assumed). Appending "Attacker loses a stress point" to a sentence
 *    whose subject is the attacker firing at someone else would read as the target's loss.
 *  - **One clause, in a fixed precedence.** Destruction outranks an interception outranks a
 *    structure loss outranks a stress loss: a mech that lost a point and then died is described by
 *    the death.
 *  - **Never on an AREA story line.** That sentence already carries one outcome PER caught target
 *    and has no single subject — the same reason {@link NarratedResolution.outcome} is absent
 *    there (2.1).
 */
function cascadeClause(steps: readonly ResolvedStep[], target: CombatantId, names: NameOf): string {
  const mine = <T extends ResolvedStep>(pred: (s: ResolvedStep) => s is T): T[] => steps.filter(pred).filter((s) => (s as { target?: CombatantId }).target === target);
  const destroyed = mine((s): s is DestroyStep => s.op === "destroy").some((s) => s.destroyed);
  if (destroyed) return ` ${names(target)} is destroyed.`;
  const ignored = mine((s): s is InterceptStep => s.op === "intercept").find((s) => s.ignored);
  if (ignored) return ` ${ignored.source.name} shrugs off the structure loss.`;
  for (const op of ["structure_delta", "stress_delta"] as const) {
    const net = mine((s): s is StructureDeltaStep | StressDeltaStep => s.op === op).reduce((sum, s) => sum + s.delta, 0);
    if (net < 0) {
      const n = Math.abs(net);
      return ` ${names(target)} loses ${n} ${trackWord(op)} point${n === 1 ? "" : "s"}.`;
    }
  }
  return "";
}

/**
 * `<actor> fires the <weapon>[ (Blast N)] at <target> — <OUTCOME>[, N damage].` — or, for a tech
 * attack, `<actor> makes a tech attack on <target> — <OUTCOME>.` See {@link attackClause} for the
 * three verb shapes and why a tech attack's is generic. `N` sums every non-heat, non-immune
 * `DamageStep.final` in the resolution (the weapon's own hit plus any rider) — heat is excluded
 * (heat is never damage); an immune hit dealt no damage by definition, and a tech attack rolls no
 * weapon damage at all, so its sentence ends at the outcome.
 */
function buildStory(args: { head: SegmentHead; about: ResolutionAbout | undefined; names: NameOf; steps: readonly ResolvedStep[] }): string {
  const { head, about, names, steps } = args;
  const actorName = names(about?.actor ?? head.attacker);
  const targetName = names(head.target);
  const verbClause = attackClause(head, about, targetName);
  // A `miss_chance` head IS the outcome — the shot missed outright before any d20, so there is
  // no damage clause to compute (an early-out emits no `damage` step at all).
  const totalDamage = damageDealt(steps.filter((s): s is DamageStep => s.op === "damage"));
  const damageClause = head.op === "to_hit" && totalDamage > 0 ? `, ${totalDamage} damage` : "";
  // The cascade's second sentence (3.2/3.3b) — see {@link cascadeClause} for the rule it settles.
  return `${actorName} ${verbClause} — ${outcomeWord(headOutcome(head))}${damageClause}.${cascadeClause(steps, head.target, names)}`;
}

/**
 * The **area** story line (`resolution-narration` 2.1): `<actor> fires the <weapon> (Blast N) —
 * <T1> HIT 7, <T2> MISS, <T3> CRITICAL HIT 9.` One sentence naming the shape and every target the
 * template actually caught, each with its OWN outcome and its OWN damage — never a cross-target
 * total anywhere (`projects/completed/aoe-attack-headline.md`'s invariant; the original defect that
 * project fixed was exactly a headline naming one target and summing damage across all of them).
 *
 * **No target/origin clause.** `about.targets` carries the DECLARED aim, which for a cell-targeted
 * area attack is absent entirely (measured off a real Howitzer and a real Mortar Blast: `about` was
 * `{actor, action, weapon}` with no `targets` key) and for a unit-aimed one just repeats a name the
 * caught list already prints. The aimed CELL is not on the record at all. So the sentence names who
 * was caught and nothing else — the honest degradation, not an invented origin phrase.
 *
 * Degrades to `<actor> attacks` (no weapon, no shape) when `about.weapon` is absent, and reads
 * `<actor> makes a tech attack` for an area-shaped tech attack — see {@link attackClause}, which
 * both story builders share so the verb cannot drift between the single-target and area forms.
 */
function buildAreaStory(args: { segments: readonly ResolvedStep[][]; about: ResolutionAbout | undefined; names: NameOf }): string {
  const { segments, about, names } = args;
  const first = segments[0]![0] as SegmentHead;
  const actorName = names(about?.actor ?? first.attacker);
  // Each run of consecutive targets rolled the same way shares one verb clause (3.2): a fan-out
  // whose heads disagree on `vs` reads `fires the Mortar (Blast 1) — T1 HIT 7; makes a tech attack
  // — T2 MISS` rather than naming the first head's verb over a target that rolled against E-Def.
  // Every shipped fan-out is one run, so this is the single-clause sentence it always was.
  const runs: { verb: string; targets: string[] }[] = [];
  for (const seg of segments) {
    const head = seg[0] as SegmentHead;
    const verb = attackClause(head, about, undefined);
    const dealt = damageDealt(seg.filter((s): s is DamageStep => s.op === "damage"));
    const target = `${names(head.target)} ${outcomeWord(headOutcome(head))}${dealt > 0 ? ` ${dealt}` : ""}`;
    const last = runs[runs.length - 1];
    if (last !== undefined && last.verb === verb) last.targets.push(target);
    else runs.push({ verb, targets: [target] });
  }
  return `${actorName} ${runs.map((r) => `${r.verb} — ${r.targets.join(", ")}`).join("; ")}.`;
}

// ── head-less resolutions (narration-ledger-retirement 3.1) ─────────────────────────────

/**
 * How one head-less kind reads in its story line (Decision 1, option A): `phrase` is the verb
 * phrase after the subject, and `object` optionally names the combatant it acted on, read off the
 * record. The sentence is `<subject> <phrase>[ <object>][ the <item>]`, or, when `prep` is set,
 * `<subject> <phrase>[ the <item>][ <prep> <object>]` (`uses the Frag Grenades on Assault`).
 * `itemIsSubject` makes the item the sentence's subject instead (a mine goes off by itself).
 */
type HeadlessPhrase = {
  phrase: string | ((steps: readonly ResolvedStep[]) => string);
  object?: (steps: readonly ResolvedStep[], about: ResolutionAbout | undefined) => CombatantId | undefined;
  prep?: string;
  itemIsSubject?: true;
};

/** A reaction's verb phrase: the status its steps add (`gains Braced`), else `reacts`. */
function reactionPhrase(steps: readonly ResolvedStep[]): string {
  const added = steps.find((s): s is StatusAddStep => s.op === "status_add");
  return added !== undefined ? `gains ${added.status}` : "reacts";
}

/** The first contest's other party, from the actor's side of it. */
function contestOpponent(steps: readonly ResolvedStep[], about: ResolutionAbout | undefined): CombatantId | undefined {
  const contest = steps.find((s): s is ContestStep => s.op === "contest");
  if (contest === undefined) return undefined;
  return contest.challenger === about?.actor ? contest.defender : contest.challenger;
}

/** The partner a status step pairs the actor with (Riding/Ridden, Unpiloted's pilot, a jockey's mount). */
function statusPartner(steps: readonly ResolvedStep[], about: ResolutionAbout | undefined): CombatantId | undefined {
  for (const s of steps) {
    if (s.op !== "status_add" && s.op !== "status_remove") continue;
    if (s.target === about?.actor && s.partner !== undefined) return s.partner;
    if (s.partner === about?.actor) return s.target;
  }
  return undefined;
}

/** The first unit a `forced_move` step displaces (the grapple drag's partner). */
function displacedUnit(steps: readonly ResolvedStep[]): CombatantId | undefined {
  return steps.find((s) => s.op === "forced_move")?.target;
}

/** The first declared target, or the first unit a status step lands on that is not the actor. */
function firstTarget(steps: readonly ResolvedStep[], about: ResolutionAbout | undefined): CombatantId | undefined {
  if (about?.targets?.[0] !== undefined) return about.targets[0];
  return steps.find((s): s is StatusAddStep => s.op === "status_add" && s.target !== about?.actor)?.target;
}

/** `tries to shake off Stunned[ and Jammed]` off the re-save steps' recorded `ends` (`SaveStep.ends`). */
function saveEndsPhrase(steps: readonly ResolvedStep[]): string {
  const statuses = steps.flatMap((s) => (s.op === "save" && s.ends !== undefined ? [s.ends] : []));
  return statuses.length === 0 ? "rolls a save-ends check" : `tries to shake off ${statuses.join(" and ")}`;
}

/** `deals 5 kinetic` off the GM's recorded adjudication (the witness `damage` step `refereeGmDamage` records), else `deals damage`. */
function gmDamagePhrase(steps: readonly ResolvedStep[]): string {
  const adjudged = steps.find((s): s is DamageStep => s.op === "damage");
  return adjudged === undefined ? "deals damage" : `deals ${adjudged.preMitigation} ${adjudged.dtype}${adjudged.ap ? " (AP)" : ""}`;
}

/**
 * **The phrase table** — one row per head-less resolution kind (`about.action`, or the rider's
 * `kind` for an entry recorded before Phase 2). Adding a kind is one row. A kind with no row
 * reads `<subject> acts`, the honest degradation, never a titled id.
 */
const HEADLESS_PHRASES: Readonly<Record<string, HeadlessPhrase>> = {
  // A reaction's own link (`chained-resolution-sequence` 4.3): what the reactor gained, when it
  // gained a status (`pc1 gains Braced`), else that it reacted.
  reaction: { phrase: reactionPhrase },
  hide: { phrase: "hides" },
  shutdown: { phrase: "shuts down" },
  boot_up: { phrase: "boots up" },
  self_destruct: { phrase: "starts a self-destruct countdown" },
  disengage: { phrase: "disengages" },
  engage_compcon: { phrase: "engages its COMP/CON" },
  take_back_compcon: { phrase: "takes control back from its COMP/CON" },
  reload: { phrase: "reloads" },
  eject: { phrase: "ejects" },
  dismount: { phrase: "dismounts" },
  mount: { phrase: "mounts", object: statusPartner },
  ride: { phrase: "climbs onto", object: statusPartner },
  ride_dismount: { phrase: "climbs off", object: statusPartner },
  jockey: { phrase: "jockeys", object: contestOpponent },
  jockey_shake_off: { phrase: "shakes off", object: contestOpponent },
  jockey_distract: { phrase: "distracts", object: firstTarget },
  search: { phrase: "searches", prep: "for", object: contestOpponent },
  grapple_contest: { phrase: "contests the grapple", prep: "with", object: contestOpponent },
  grapple_break: { phrase: "breaks free of the grapple" },
  grapple_release: { phrase: "releases the grapple" },
  grapple_drag: { phrase: "drags", object: displacedUnit },
  grapple_end: { phrase: "breaks adjacency, ending the grapple", prep: "with", object: statusPartner },
  deploy_drone: { phrase: "deploys" },
  deploy_mine: { phrase: "lays" },
  detect_mine: { phrase: "sweeps for mines" },
  disarm_mine: { phrase: "disarms" },
  mine_detonation: { phrase: "detonates", itemIsSubject: true },
  gm_damage: { phrase: gmDamagePhrase, prep: "to", object: firstTarget },
  bulldoze_sweep: { phrase: "bulldozes through" },
  stabilize: { phrase: "stabilizes" },
  overcharge: { phrase: "overcharges" },
  full_tech: { phrase: "takes a Full Tech action", prep: "on", object: firstTarget },
  quick_tech: { phrase: "takes a Quick Tech action", prep: "on", object: firstTarget },
  damage_trigger: { phrase: "triggers an effect" },
  action_taken_trigger: { phrase: "triggers an effect" },
  lifecycle_trigger: { phrase: "triggers an effect" },
  // The dice-rolled turn-edge ticks (orchestrator ruling 2026-09-25, option B).
  save_ends: { phrase: saveEndsPhrase },
  burn_tick: { phrase: "tries to put out the burn" },
  meltdown_blast: { phrase: "melts down" },
};

/**
 * An activation's raw affordance key (`pg_frag_grenades:0`) is not a verb: it reads `uses the
 * <item>`, or `uses an ability` when no name was frozen (a frame trait, a talent, or an entry
 * recorded before `about.item` existed). Never a titled id.
 */
const ACTIVATION_PHRASE: HeadlessPhrase = { phrase: "uses", prep: "on", object: firstTarget };
const UNNAMED_ACTIVATION_PHRASE: HeadlessPhrase = { phrase: "uses an ability", prep: "on", object: firstTarget };

/**
 * The outcome clause after the dash, read off the record: a contest the actor took part in reads
 * `WINS`/`LOSES` from the actor's side; a single save by the actor or by the story's object reads `PASS`/`FAIL`; any other
 * saves read each roller's verdict (`Assault 1 FAIL, Assault 2 PASS`), or each status's for one
 * roller's several re-saves. Nothing rolled, no clause.
 */
function headlessOutcome(args: { steps: readonly ResolvedStep[]; about: ResolutionAbout | undefined; object: CombatantId | undefined; names: NameOf }): string {
  const { steps, about, object, names } = args;
  const contest = steps.find((s): s is ContestStep => s.op === "contest");
  if (contest !== undefined) {
    const actorWon = contest.challenger === about?.actor ? contest.winner === "challenger" : contest.winner === "defender";
    return actorWon ? "WINS" : "LOSES";
  }
  const saves = steps.filter((s): s is SaveStep => s.op === "save");
  // One save by the subject or the named object reads bare; one by someone else names them (a meltdown's caught drone).
  if (saves.length === 1 && (about === undefined || saves[0]!.actor === about.actor || saves[0]!.actor === object)) return saves[0]!.result === "pass" ? "PASS" : "FAIL";
  // Several re-saves by ONE roller read by the status each tried to end; several rollers by name.
  return saves.map((s) => `${s.ends ?? names(s.actor)} ${s.result === "pass" ? "PASS" : "FAIL"}`).join(", ");
}

/** The story line's subject: the GM for `GM_ACTOR`, otherwise the actor's name. */
function subjectName(actor: CombatantId, names: NameOf): string {
  return actor === GM_ACTOR ? "GM" : names(actor);
}

/**
 * The story line for a resolution with no attack roll (Decision 1, option A):
 * `<subject> <phrase>[ <object>][ the <item>][ — <outcome>].[ <standing change>]` — `Bret uses the Frag Grenades —
 * Assault 1 FAIL, Assault 2 PASS.`, `Little Red Bus breaks free of the grapple — WINS.`, `The
 * Thermite Mine detonates — Ronin FAIL.`, `GM deals damage to Assault.`
 *
 * An entry recorded before `about` existed has no subject; it reads as its kind, capitalised
 * (`Grapple break — WINS.`), which says what happened without inventing who.
 */
function buildHeadlessStory(args: { steps: readonly ResolvedStep[]; kind?: string; about: ResolutionAbout | undefined; names: NameOf }): string {
  const { steps, kind, about, names } = args;
  const action = about?.action ?? kind;
  if (about === undefined) {
    const outcome = headlessOutcome({ steps, about, object: undefined, names });
    const tail = outcome === "" ? "." : ` — ${outcome}.`;
    const word = (action ?? "resolution").replace(/_/g, " ");
    return `${word[0]!.toUpperCase()}${word.slice(1)}${tail}`;
  }
  const activation = about.item !== undefined ? ACTIVATION_PHRASE : action?.includes(":") ? UNNAMED_ACTIVATION_PHRASE : undefined;
  const entry = (action !== undefined ? HEADLESS_PHRASES[action] : undefined) ?? activation ?? { phrase: "acts" };
  const phrase = typeof entry.phrase === "string" ? entry.phrase : entry.phrase(steps);
  const itemIsSubject = entry.itemIsSubject === true && about.item !== undefined;
  const object = itemIsSubject ? undefined : entry.object?.(steps, about);
  const outcome = headlessOutcome({ steps, about, object, names });
  const tail = outcome === "" ? "." : ` — ${outcome}.`;
  if (itemIsSubject) return `The ${about.item!.name} ${phrase}${tail}`;
  const itemClause = about.item !== undefined ? ` the ${about.item.name}` : "";
  const objectClause = object === undefined ? "" : entry.prep !== undefined ? ` ${entry.prep} ${names(object)}` : ` ${names(object)}`;
  const middle = entry.prep !== undefined ? `${itemClause}${objectClause}` : `${objectClause}${itemClause}`;
  // The one further clause when the object's standing changed (3.2's rule, {@link cascadeClause}):
  // `GM deals 10 kinetic to Bulwark. Bulwark loses 1 structure point.`
  const cascade = object !== undefined ? cascadeClause(steps, object, names) : "";
  return `${subjectName(about.actor, names)} ${phrase}${middle}${tail}${cascade}`;
}

/** The mine checks' outcome copy (Core p.106), registered in `frontend/src/rulesCopyRegistry.ts`. Moved from `ResultCard.tsx`'s retired ledger (`narration-ledger-retirement` 5.1). */
export const DETECT_FAILED_NOTE = "Detects nothing.";
export function detectPassedNote(revealed: number): string {
  if (revealed === 0) return "No hidden mines within Sensors.";
  return `Revealed ${revealed} hidden mine${revealed === 1 ? "" : "s"}.`;
}
export const MINE_DISARMED_NOTE = "The mine is disarmed and removed.";
export const MINE_DETONATES_NOTE = "The mine detonates.";

/** A zero-step activation that RAN (`decisions/0162`): the throw was charged and did nothing. */
export const NO_EFFECT_NOTE = "No effect.";

/**
 * The closing row a head-less receipt adds when the steps alone do not say what happened.
 *
 *  - **A mine check** (Core p.106) reads its outcome off the SYSTEMS `save`: a failed Detect Mines
 *    records only the save whether or not a mine was in range (`resolveMineDetection`'s oracle
 *    rule), so it says one fixed sentence; a pass counts the `concealed_set` reveals it carries.
 *  - **A zero-step activation** reads `No effect.` It ran and was charged (`decisions/0162`), and
 *    an empty receipt would read as a card with nothing to say.
 */
function headlessNoteRow(steps: readonly ResolvedStep[], action: string | undefined, isActivation: boolean): ReceiptRow | undefined {
  if (action === "detect_mine" || action === "disarm_mine") {
    const pass = steps.some((s) => s.op === "save" && s.result === "pass");
    if (action === "disarm_mine") return { label: "MINE", text: pass ? MINE_DISARMED_NOTE : MINE_DETONATES_NOTE };
    const revealed = steps.filter((s) => s.op === "concealed_set" && !s.concealed).length;
    return { label: "MINES", text: pass ? detectPassedNote(revealed) : DETECT_FAILED_NOTE };
  }
  if (isActivation && steps.length === 0) return { label: "RESULT", text: NO_EFFECT_NOTE };
  return undefined;
}

// ── the recorded-order walk ────────────────────────────────────────────────────────────────

/**
 * Every row a step list yields when read in recorded order, outside any `vs <target>` block: the
 * trailing consequences of an attack, the preamble before its first block (3.2), and the whole
 * receipt of a head-less resolution (3.1).
 *
 * `loose` adds the two ops that otherwise only render inside a block: `damage` (a DAMAGE/HEAT row,
 * with ARMOR/RESIST/IMMUNE for a named hit on a named target) and a negative `hp_delta` (a RESULT
 * row for that step). A mine's burst, a thrown grenade and the GM's damage record them with no
 * `to_hit` to hang a block off.
 */
function recordedOrderRows(args: { steps: readonly ResolvedStep[]; about: ResolutionAbout | undefined; names: NameOf; loose: boolean; nextGroupId: () => number }): RowSpec[] {
  const { steps, about, names, loose, nextGroupId } = args;
  const rows: RowSpec[] = [];
  const paired = chargeSpentFor(steps);
  let damageGroup: { label: "DAMAGE" | "HEAT"; id: number } | undefined;
  steps.forEach((step, i) => {
    if (step.op !== "damage") damageGroup = undefined;
    if (loose && step.op === "damage") {
      const label = labelOf(step);
      const opens = damageGroup === undefined || damageGroup.label !== label;
      if (opens) damageGroup = { label, id: nextGroupId() };
      rows.push({ pending: damageParts(step, { rolled: true }), label: opens ? label : "", sub: !opens, groupId: damageGroup!.id });
      if (label === "DAMAGE" && isNamed(step) && step.target !== undefined) {
        const targetName = names(step.target);
        if (step.immune) rows.push(immuneRow(step, targetName));
        else {
          rows.push(armorRow(step, targetName, false));
          if (step.resisted) rows.push(resistRow(step, targetName));
        }
      }
      return;
    }
    if (loose && step.op === "hp_delta" && step.delta < 0) {
      const rr = resultRow(step.target, [step], names);
      if (rr) rows.push(rr);
      return;
    }
    if (step.op === "save") {
      rows.push(saveRow(step, names));
      const prov = saveProvenanceRow(step, names);
      if (prov) rows.push(prov);
      return;
    }
    // A `contest` (3.4) is the second two-roll shape the engine records, and it rides this walk
    // for the same reasons a `save` does: it names its own two parties, and its sub-rows are the
    // per-side draws.
    if (step.op === "contest") {
      rows.push(...contestRows(step, names));
      return;
    }
    if (CONSEQUENCE_OPS.has(step.op)) {
      const row = consequenceRow(step, about, names);
      if (row) rows.push(row);
      return;
    }
    // `cascadeRow` first (3.2/3.3b), then the displacement/burn and remaining-consequence rows
    // 3.4 added. The order matters only for `affordance_use_set`, where `cascadeRow` returns
    // `undefined` DELIBERATELY for a charge already absorbed into a `PAINT JOB` row —
    // `residueRow` does not handle that op, so the chain cannot resurrect it.
    const row = cascadeRow({ step, names, paired, index: i }) ?? residueRow(step, about, names);
    if (row) rows.push(row);
  });
  return rows;
}

// ── the entry point ────────────────────────────────────────────────────────────────────────

/** A row with its structured cells stripped — the default, unstructured receipt shape. */
function withoutCells(row: ReceiptRow): ReceiptRow {
  if (row.value === undefined && row.detail === undefined && row.accuracy === undefined && row.noChange === undefined) return row;
  const { value: _value, detail: _detail, accuracy: _accuracy, noChange: _noChange, ...rest } = row;
  return rest;
}

/**
 * Narrate any committed resolution off its recorded step list and `about` breadcrumb alone.
 *
 *  - **A single-target attack** (Phase 1, `resolution-narration` 1.3): one story line, one receipt.
 *  - **An AREA fan-out** (2.1): the same receipt once per caught target under a `vs <name>` divider,
 *    and one story line naming the shape and every target's own outcome. Steps recorded before the
 *    first head open the receipt as a preamble (`narration-ledger-retirement` 3.2).
 *  - **A head-less resolution** (3.1): no `to_hit` and no `miss_chance` — a Search, a thrown
 *    grenade, a mine going off, the GM's damage. Its story comes off {@link HEADLESS_PHRASES}; its
 *    receipt is every step in recorded order.
 *  - **A chain** (`chained-resolution-sequence` 1.2, `decisions/0210`): `link` steps split the list
 *    into links, each narrated as above under its own actor and weapon. The story is one sentence
 *    per link, each after the first led by its cause (`Aggression (random target, d3 = 1): …`);
 *    the receipt opens each later link with a divider (`Aggression · Berserker`) and its RANDOM row.
 *
 * Total since 3.3: it never returns `undefined`, and there is no ledger to fall back to.
 *
 * `kind` is read only by a head-less resolution recorded before `about.action` existed; a Barrage
 * shot's framing (2.2) comes off `about.barrage`/`about.noBonus`, because `kind` does not reach the
 * CLI (`cli/src/encounter-session.ts` captures `about`, never `kind`).
 */
export function narrateResolution(args: NarrateArgs): NarratedResolution {
  if (!args.steps.some((s) => s.op === "link")) return narrateLink(args);
  const names: NameOf = args.names ?? ((id) => id);
  const stories: string[] = [];
  const receipt: ReceiptRow[] = [];
  let outcome: NarratedResolution["outcome"];
  chainParts(args.steps).forEach((part, n) => {
    const link = part.link;
    const narrated = narrateLink({ ...args, steps: part.steps, ...(link !== undefined ? { about: linkAbout(link) } : {}) });
    if (n === 0) outcome = narrated.outcome;
    stories.push(link === undefined ? narrated.story : `${linkLead(link)}: ${narrated.story}`);
    if (link !== undefined) {
      receipt.push({ label: "", text: linkHeading(link, names), divider: true });
      const pick = pickRow(link, names);
      if (pick !== undefined) receipt.push(pick);
    }
    receipt.push(...narrated.receipt);
    // Each reaction that interrupted this part (4.3): its own sentence and block, after the part's.
    for (const reaction of part.reactions) {
      const told = narrateLink({ ...args, steps: reaction.steps, about: { actor: reaction.link.actor, action: "reaction" } });
      stories.push(`${linkCauseName(reaction.link)}: ${told.story}`);
      receipt.push({ label: "", text: linkHeading(reaction.link, names), divider: true });
      receipt.push(...told.receipt);
    }
  });
  return { story: stories.join(" "), ...(outcome !== undefined ? { outcome } : {}), receipt };
}

/** {@link narrateResolution}'s arguments. */
type NarrateArgs = {
  steps: readonly ResolvedStep[];
  kind?: string;
  about?: ResolutionAbout;
  names?: NameOf;
  /**
   * `true` keeps each row's structured {@link ReceiptRow.value}/{@link ReceiptRow.detail} cells
   * (`comms-attack-reaction-cards` 2.2), plus {@link ReceiptRow.accuracy} and
   * {@link ReceiptRow.noChange} (3.2), for a renderer that lays the receipt out as a grid.
   * Omitted, the rows carry exactly `label`/`text`/`sub`/`divider`/`emphasis`, as they always
   * have — the CLI prints `text` alone, and the golden tests pin those rows whole.
   */
  structured?: boolean;
};

// ── chains (chained-resolution-sequence 1.2, `decisions/0210`) ──────────────────────────────

/**
 * The `link` steps of a chained resolution, with their indices. Each link runs from its marker to
 * the next one; the steps before the first are the primary action `about` describes. Exported for
 * the director and the glance, which split the same way.
 */
/** One part of a chained resolution: the primary action or a link's, with the reactions that interrupted it. */
export type ChainPart = { link?: LinkStep; steps: ResolvedStep[]; reactions: { link: LinkStep; steps: ResolvedStep[] }[] };

/**
 * A chained resolution cut into its parts (`chained-resolution-sequence` 4.3): the primary action,
 * then one per `forced_attack`/`drone_field` link. A `reaction` link's steps (up to its `resume`)
 * become a reaction of the part it interrupted, and the steps after the `resume` rejoin that part,
 * so an attack's damage stays one attack even when a Brace landed between its roll and its damage.
 * Every reader of a chain cuts it this way: the narrator, the COMMS glance, the board's director.
 */
export function chainParts(steps: readonly ResolvedStep[]): ChainPart[] {
  const parts: ChainPart[] = [{ steps: [], reactions: [] }];
  let reaction: { link: LinkStep; steps: ResolvedStep[] } | undefined;
  for (const step of steps) {
    const part = parts[parts.length - 1]!;
    if (step.op === "link" && step.cause === "reaction") {
      reaction = { link: step, steps: [] };
      part.reactions.push(reaction);
    } else if (step.op === "link" && step.cause === "resume") {
      reaction = undefined;
    } else if (step.op === "link") {
      reaction = undefined;
      parts.push({ link: step, steps: [], reactions: [] });
    } else (reaction ?? part).steps.push(step);
  }
  return parts;
}

export function chainLinks(steps: readonly ResolvedStep[]): { link: LinkStep; at: number }[] {
  const out: { link: LinkStep; at: number }[] = [];
  steps.forEach((step, at) => {
    if (step.op === "link") out.push({ link: step, at });
  });
  return out;
}

/**
 * The breadcrumb a link's own block narrates under: its actor, weapon and target stand in for
 * `about`'s. A class-tier-hidden weapon is left out, as the room leaves it out of `about`, so the
 * story reads `Berserker attacks Elite Cataphract` rather than naming the sentinel.
 */
function linkAbout(link: LinkStep): ResolutionAbout {
  const weapon = link.weapon !== undefined && link.weapon.name !== UNDISCLOSED_WEAPON_NAME ? link.weapon : undefined;
  return { actor: link.actor, ...(weapon !== undefined ? { weapon } : {}), ...(link.target !== undefined ? { targets: [link.target] } : {}) };
}

/** What a link's cause reads as when no named feature fired it. */
const CAUSE_WORDS: Record<LinkStep["cause"], string> = { forced_attack: "Forced attack", drone_field: "Drone field", reaction: "Reaction", resume: "Resumes" };

/**
 * The link's feature name, titled (`npcf_aggression_berserker` → `Aggression`), or its cause in
 * words when no feature fired it or the viewer's disclosure hides it.
 */
export function linkCauseName(link: LinkStep): string {
  return link.feature !== undefined && link.feature.name !== UNDISCLOSED_FEATURE_NAME ? displaySourceName(link.feature.name) : CAUSE_WORDS[link.cause];
}

/** `Aggression (random target, d3 = 1)` — the words that open a link's sentence in the story. */
function linkLead(link: LinkStep): string {
  const pick = link.pick;
  if (link.repeat) return `${linkCauseName(link)} (repeats the attack)`;
  if (pick === undefined) return linkCauseName(link);
  return pick.roll !== undefined ? `${linkCauseName(link)} (random target, d${pick.from.length} = ${pick.roll})` : `${linkCauseName(link)} (the only candidate)`;
}

/** The divider that opens a link's block of receipt rows: `Aggression · Berserker`. */
function linkHeading(link: LinkStep, names: NameOf): string {
  return `${linkCauseName(link)} · ${names(link.actor)}`;
}

/** The RANDOM row a picked link opens with: the die, the face and the candidates it chose among. */
function pickRow(link: LinkStep, names: NameOf): ReceiptRow | undefined {
  const pick = link.pick;
  if (pick === undefined || link.target === undefined) return undefined;
  const among = pick.from.map(names).join(", ");
  return pick.roll !== undefined
    ? { label: "RANDOM", text: `d${pick.from.length} = ${pick.roll} → ${names(link.target)} (of ${among})` }
    : { label: "RANDOM", text: `${names(link.target)}, the only candidate` };
}

/** One link's narration: everything {@link narrateResolution} did before chains existed. */
function narrateLink(args: NarrateArgs): NarratedResolution {
  const { steps, about } = args;
  const names: NameOf = args.names ?? ((id) => id);
  const finish = (rows: readonly RowSpec[]): ReceiptRow[] => (args.structured === true ? padReceipt(rows) : padReceipt(rows).map(withoutCells));
  // Damage-block ids run across the WHOLE receipt, never restarting per target — two different
  // targets' DAMAGE rows must never share a padding group (they are separated by a divider row
  // anyway, but the id is the actual guarantee; see `padReceipt`).
  let groupCounter = 0;
  const nextGroupId = () => groupCounter++;

  // One segment per target — a single-target attack has exactly one, an area fan-out one per
  // caught target (2.1). A segment opens at a `to_hit` or (2.5) at a `miss_chance`, the coin
  // that stands in for a to-hit roll the attack never got to make. A single-segment resolution
  // is narrated off the WHOLE step list rather than the segment, so Phase 1's output is
  // byte-identical to what it was before segmentation existed even in the (never-observed) case
  // of a step preceding the only head.
  const { leading, segments } = segmentAttack(steps);

  // No segment head at all: a resolution with no attack roll (narration-ledger-retirement 3.1).
  // Its receipt is the whole list in recorded order, and its story comes off the phrase table.
  if (segments.length === 0) {
    const action = about?.action ?? args.kind;
    // An activation records its raw affordance key as `about.action` (`pg_frag_grenades:0`); an
    // entry from before Phase 2 has only the rider's `kind: "action"`. A deploy names an item too,
    // but it is not an activation and records no steps by design.
    const isActivation = action === "action" || (action?.includes(":") ?? false);
    const note = headlessNoteRow(steps, action, isActivation);
    const receipt = finish([...recordedOrderRows({ steps, about, names, loose: true, nextGroupId }), ...(note !== undefined ? [note] : [])]);
    return { story: buildHeadlessStory({ steps, ...(args.kind !== undefined ? { kind: args.kind } : {}), about, names }), receipt };
  }

  const multi = segments.length > 1;
  const blocksOf: readonly { head: SegmentHead; steps: readonly ResolvedStep[] }[] = multi
    ? segments.map((seg) => ({ head: seg[0] as SegmentHead, steps: seg }))
    : [{ head: segments[0]![0] as SegmentHead, steps }];

  // Built as `RowSpec[]` — a DAMAGE/HEAT row starts life PENDING (unpadded content only);
  // `padReceipt` is the one place that resolves every pending row into its final, padded
  // `ReceiptRow.text` (F, resolution-narration 1.4/1.5 close-out — see `padReceipt`'s doc comment
  // for why content and presentation are split this way).
  const receiptSpecs: RowSpec[] = [];
  // The Barrage / Aux-rider framing rows (2.2) open the receipt ONCE, ahead of everything —
  // they are facts about the SHOT, not about any one target, so they never repeat per `vs
  // <target>` block even when the barraged weapon is an area template that caught several.
  receiptSpecs.push(...chainRows(about));
  // Steps recorded before the first of SEVERAL heads belong to no target's block (3.2): they open
  // the receipt as a preamble, in recorded order. (With one head the whole list is that head's
  // block, as above, so nothing is left over.)
  const preamble = multi ? leading : [];
  receiptSpecs.push(...recordedOrderRows({ steps: preamble, about, names, loose: true, nextGroupId }));

  for (const { head, steps: segment } of blocksOf) {
    // The per-target divider (2.1) — omitted entirely for a single-target attack, which keeps
    // Phase 1's receipt exactly as it was.
    if (multi) receiptSpecs.push({ label: "", text: `vs ${names(head.target)}`, divider: true });
    // A `miss_chance` head is the target's whole outcome: the coin came up the miss face and the
    // resolution early-outs before the d20, so there is no roll, no damage and no HP change to
    // itemize — one row, and on to the next target.
    if (head.op === "miss_chance") {
      receiptSpecs.push(missChanceRow(head, names));
      continue;
    }
    const toHit = head;
    receiptSpecs.push(toHitRow(toHit));
    const accRow = accuracyProvenanceRow(toHit, names);
    if (accRow) receiptSpecs.push(accRow);

    const damageSteps = segment.filter((s): s is DamageStep => s.op === "damage");

    // Group into contiguous DAMAGE/HEAT blocks (a weapon's own hit plus any rider that rode the
    // same attack share one block and one label, per the spec's own worked examples).
    const blocks: DamageStep[][] = [];
    for (const step of damageSteps) {
      const last = blocks[blocks.length - 1];
      if (last && labelOf(last[0]!) === labelOf(step)) last.push(step);
      else blocks.push([step]);
    }

    for (const block of blocks) {
      const label = labelOf(block[0]!);
      const groupId = nextGroupId();
      block.forEach((step, i) => {
        receiptSpecs.push({ pending: damageParts(step), label: i === 0 ? label : "", sub: i > 0, groupId });
      });

      if (label === "HEAT") continue; // heat bypasses armor/resist/immune/RESULT entirely — it's heat, not HP.

      const hasRider = block.some((s) => !isNamed(s));
      for (const step of block) {
        if (!isNamed(step)) continue; // an anonymous/rider damage step bypasses mitigation — no row of its own.
        const targetName = names(step.target ?? toHit.target);
        if (step.immune) {
          receiptSpecs.push(immuneRow(step, targetName));
          continue;
        }
        receiptSpecs.push(armorRow(step, targetName, hasRider));
        if (step.resisted) receiptSpecs.push(resistRow(step, targetName));
      }
    }

    // RESULT reads only THIS segment's own `hp_delta` steps — the aoe-attack-headline invariant
    // (no cross-target total ever lands on a per-target row) holds by construction here.
    const rr = resultRow(toHit.target, segment.filter((s): s is HpDeltaStep => s.op === "hp_delta"), names);
    if (rr) receiptSpecs.push(rr);
  }

  // The trailing CONSEQUENCES block (2.5) — what the shot spent, moved, or landed, in RECORDED
  // order, read off the WHOLE step list once rather than per `vs <target>` block. See
  // `CONSEQUENCE_OPS` for why it is not per-segment.
  //
  // **A forced `save` rides this same walk (3.1), in recorded order alongside the consequences —
  // which is what puts the SAVE row immediately ahead of the STATUS row its failure landed.** The
  // engine records `save` then its `on_fail` branch's grounded steps (`interpretForceSave`), so
  // reading the list in order reproduces cause-then-effect for free rather than by an ordering
  // rule this module would have to keep true. It belongs in the trailing block for the same
  // reason the consequence ops do: every save row names its own roller, and on an area fan-out
  // the caught targets' saves would otherwise have to be split back across `vs <target>` blocks.
  //
  // **The structure/stress CASCADE rides the same walk (3.2 + 3.3b)**, for the same two reasons:
  // recorded order is what puts the `STRUCTURE` point-taken row ahead of the `PAINT JOB` row that
  // tried to un-take it and the table row that followed when it did not, and every cascade row
  // names its own subject (a `tg_heat_self` overheat lands on the ATTACKER, not on any caught
  // target, so a per-segment placement would file it under the wrong heading).
  receiptSpecs.push(...recordedOrderRows({ steps: steps.slice(preamble.length), about, names, loose: false, nextGroupId }));

  const receipt = finish(receiptSpecs);
  if (multi) return { story: buildAreaStory({ segments, about, names }), receipt };

  const head = blocksOf[0]!.head;
  return { story: buildStory({ head, about, names, steps }), outcome: headOutcome(head), receipt };
}
